/** Jerry API client — fetch wrapper with timeout, auth, and error handling. */

import { useAuthStore } from "../stores/auth";
import type {
  Client,
  Matter,
  MatterBrief,
  MatterStageInstance,
  Task,
  Document,
  Conversation,
  ConversationSearchResult,
  ConversationDocumentsScope,
  Message,
  CapabilityRun,
  Member,
  CompassData,
  PaginatedResponse,
  User,
  Lawyer,
  Deadline,
  TimeEntry,
  TimeEntrySummary,
  TimeEntryCreatePayload,
  TimeEntryUpdatePayload,
  ActionType,
  StaffProfile,
  Resource,
  DocumentGrantsResponse,
  DocumentGrant,
  ExternalShare,
  ExternalShareCreated,
  PublicShareMeta,
  PublicShareContentResult,
  PublicShareState,
  PrincipalOption,
  DocOwnerScope,
  DocAccessLevel,
  EmployeeGrade,
  Department,
  Position,
  Role,
  StaffProfileCreatePayload,
  StaffProfileUpdatePayload,
  UserCreatePayload,
  UserUpdatePayload,
  DepartmentMemberAddPayload,
  AdvisorItem,
  ExtractionJob,
  ExtractionJobCreateRequest,
} from "../types";
import type {
  MattersTreeNode,
  WorkbenchView,
  WorkbenchViewConfig,
} from "../types";
import type { RedactType, RedactStatusResponse } from "../types/redact";
import type {
  Capability,
  CapabilityCreateRequest,
  CapabilityUpdateRequest,
  EngineCatalog,
} from "../types/capability";
import type {
  CustomAdvisor,
  CustomAdvisorCreateRequest,
  CustomAdvisorUpdateRequest,
} from "../types/customAdvisor";
import type {
  MemoryItem,
  MemoryCreateRequest,
  MemoryUpdateRequest,
  MemoryConfig,
} from "../types/memory";
import type {
  McpServer,
  McpServerRegisterRequest,
  McpServerSyncResponse,
  McpBindingPatch,
  McpServerTool,
  McpConnector,
} from "../types/mcp-server";
import type { Connector } from "../types/connector";
import type { PromptTemplate } from "../types/promptTemplate";
import type {
  KnowledgeItem,
  KnowledgeItemCreateRequest,
  KnowledgeItemUpdateRequest,
  KnowledgeCategory,
  KnowledgeCategoryCreateRequest,
  KnowledgeCategoryUpdateRequest,
  KnowledgeCategoryMoveRequest,
  ExternalLink,
} from "../types/knowledge";
import type {
  PipelineExecution,
  PipelineTemplate,
  PipelineTemplateDetail,
  PipelineInstanceDetail,
  PipelineInstanceListItem,
} from "../types/pipeline";
import type {
  VeridocDocumentSummary,
  ComparisonResponse,
  CreateComparisonRequest,
  DecisionStatus,
  ReviewDecisionResponse,
  AnnotationResponse,
  AnnotationListResponse,
  DocumentComparisonsResponse,
} from "../types/veridoc";
import type {
  AiFinding,
  AiFindingProcessed,
  AiReviewResult,
  AiVerdictReasonCode,
} from "../types/aiReview";
import type { CapabilityFindingVerdict } from "../types/capabilityRun";
import type {
  DueDiligenceProject,
  DueDiligenceProjectCreatePayload,
  DueDiligenceProjectListItem,
  DueDiligenceProjectListParams,
  DueDiligenceProjectUpdatePayload,
  DueDiligenceSubReport,
  DueDiligenceMessage,
  DueDiligenceMessageCreatePayload,
  DueDiligenceSubReportSaveAsDocumentPayload,
  PublicResearchSource,
  PublicResearchSourcesParams,
  PublicResearchSourcesResponse,
} from "../types/dueDiligence";
import type {
  ConflictCheck,
  ConflictCheckCreateRequest,
  ConflictCheckUpdateRequest,
  ConflictCheckStatusChangeRequest,
} from "../types/conflict";
import type {
  Draft,
  DraftCreateRequest,
  DraftSaveAsDocumentRequest,
} from "../types/draft";
import type {
  DraftDoc,
  DraftDocBodyUpdateRequest,
  DraftDocBodyUpdateResponse,
  DraftDocCreateRequest,
  DraftDocListParams,
  DraftDocSaveAsDocumentRequest,
  DraftDocUpdateRequest,
  DraftDocVersion,
  DocumentSaveAsDraftDocRequest,
  DraftDocOpenResponse,
  DraftProposal,
  DraftProposalBatch,
  DraftProposalBatchDecisionRequest,
  DraftProposalDecisionRequest,
} from "../types/draftDoc";
import type {
  Meeting,
  MeetingAiAction,
  MeetingAiActionState,
  MeetingAiActionSubmitResult,
  MeetingBookPayload,
  MeetingConnection,
  MeetingContentImportRequest,
  MeetingCreateRequest,
  MeetingListParams,
  MeetingSource,
  MeetingTaskCandidate,
  MeetingTaskCandidateConfirmResult,
  MeetingTaskCandidateDecision,
  MeetingTranscriptImportRequest,
  MeetingTranscriptResult,
  MeetingTranscriptSegmentsEditRequest,
  MeetingTranscriptSpeakerRenameRequest,
  MeetingUpdateRequest,
} from "../types/meeting";
import type {
  Calendar,
  CalendarCreate,
  CalendarUpdate,
  CalendarEvent,
  CalendarEventCreate,
  CalendarEventUpdate,
  CalendarEventUpdateResult,
  EventInstance,
  EventReminder,
  EventReminderCreate,
  EventType,
  ListEventsParams,
  UpdateEventOptions,
  DeleteEventOptions,
} from "../types/calendar";
import type { MePermissions } from "../types/permission-points";
import type {
  ScheduledTask,
  ScheduledTaskRun,
  ScheduledTaskSummary,
  ScheduledTaskCreatePayload,
  ScheduledTaskUpdatePayload,
  ScheduledRunReview,
  ScheduledRunSaveAsDocumentResult,
} from "../types/scheduledTask";
import type { AiReviewBatch } from "../types/aiReviewBatch";
import type { AiVerdictCreatePayload, AiVerdict } from "../types/aiVerdict";
import type {
  Overview,
  LegalIssue,
  LegalIssueFacets,
  CsrcFiling,
  CsrcFeedback,
  CsrcFeedbackFacets,
  TimelineSnapshot,
  HkexCase,
  HkexAnnouncement,
  SecFiling,
  SyncRun,
  SyncTriggerResponse,
  LegalIssuesParams,
  CsrcFilingsParams,
  CsrcFeedbackParams,
  HkexCasesParams,
  HkexAnnouncementsParams,
  SecFilingsParams,
  SyncRunsParams,
  TrendsData,
  TrendsMeta,
  TrendsResponse,
  HotspotsData,
  HotspotsMeta,
  HotspotsResponse,
  BenchmarksData,
  BenchmarksMeta,
  BenchmarksResponse,
  CompanySearchResult,
  CompanySearchResponse,
  CompanyBoardSummary,
  CompanyProjectStatus,
  CompanyIssue,
  CompanyFeedback,
  CompanyHkexCase,
  CompanyDetailData,
  CompanyDetailMeta,
  CompanyDetailResponse,
  BoardProfileData,
  BoardProfileMeta,
  BoardProfileResponse,
} from "../types/ipoVault";
import { AUTH_TOKEN_KEY, REFRESH_TOKEN_KEY } from "../constants/auth";
import { refreshTokenApi } from "./auth";

const API_PREFIX = "/api/v1/jerry";
const AUTH_PREFIX = "/api/v1/auth";
const AUDIT_PREFIX = "/api/v1/audit-logs";
const DEFAULT_TIMEOUT_MS = 10_000;

/** Recursively convert snake_case keys to camelCase. */
function toCamelCase<T>(obj: T): T {
  if (Array.isArray(obj)) return obj.map(toCamelCase) as T;
  if (obj !== null && typeof obj === "object") {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const camelKey = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
      result[camelKey] = toCamelCase(value);
    }
    return result as T;
  }
  return obj;
}

/** Transform a paginated response with snake_case data items to camelCase. */
function transformPaginated<T>(raw: {
  success: boolean;
  data: T[];
  meta: { page: number; pageSize: number; total: number; totalPages: number };
}): { success: boolean; data: T[]; meta: { page: number; pageSize: number; total: number; totalPages: number } } {
  return {
    ...raw,
    data: raw.data.map(toCamelCase),
  };
}

export class ApiError extends Error {
  /** Backend error code, e.g. "TIMER_STOP_DENIED". Undefined for network/timeout errors. */
  public readonly code: string | undefined;

  /**
   * Backend `error.details` payload, when present. Endpoint-specific structured
   * data (e.g. the still-indexing documents on a CHAT_DOCUMENTS_NOT_READY
   * rejection). Undefined for network/timeout errors and codes without details.
   */
  public readonly details: unknown;

  constructor(
    public readonly status: number,
    message: string,
    code?: string,
    details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.details = details;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/** Jerry API success envelope — `{ success: true, data: T }`. */
type Envelope<T> = { success: boolean; data: T };

// ---------------------------------------------------------------------------
// 401 dedup — prevent multiple concurrent redirects to /login
// ---------------------------------------------------------------------------

let isRedirecting = false;

function handle401(): void {
  if (isRedirecting) return;
  isRedirecting = true;
  useAuthStore.getState().logout();
  // Preserve the current URL so LoginPage can send the user back after a
  // successful re-auth. React Router's location.state survives only soft
  // navigations; this is a hard `window.location.href` jump, so we encode
  // the path as a query parameter instead. LoginPage reads both shapes.
  const current =
    window.location.pathname + window.location.search + window.location.hash;
  const returnTo = current || "/";
  window.location.href = `/login?from=${encodeURIComponent(returnTo)}`;
}

// ---------------------------------------------------------------------------
// Shared fetch helpers — request() 之外还有绕过 request() 的 fetch 调用
// （SSE 流式端点、需要浏览器自置 multipart boundary 的上传），鉴权头注入与
// 错误信封解析必须共享同一实现，避免各处漏掉 401 统一处理。
// ---------------------------------------------------------------------------

/** 鉴权头：localStorage 存有 token 时返回 Bearer 头，否则返回空对象。 */
export function authHeaders(): Record<string, string> {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * 统一处理非 2xx 响应：解析后端错误信封（error.message/code/details）并抛
 * `ApiError`；401 先触发统一登出与登录页跳转（经 handle401 去重）。
 * 传入 `logContext` 时（method/url/耗时），DEV 环境额外输出一条带错误码的
 * 请求级 console.error（与 request() 的成功/超时日志同格式）。
 */
export async function throwApiErrorFromResponse(
  res: Response,
  logContext?: { method: string; url: string; durationMs?: number },
): Promise<never> {
  const body = await res.json().catch(() => ({}));
  const message = body?.error?.message || `API error: ${res.status}`;
  const code = body?.error?.code as string | undefined;
  const details = body?.error?.details;

  if (import.meta.env.DEV && logContext) {
    console.error("[jerry-api]", logContext.method, logContext.url, res.status, {
      code,
      duration_ms: logContext.durationMs,
    });
  }

  if (res.status === 401) {
    handle401();
  }

  throw new ApiError(res.status, message, code, details);
}

// ---------------------------------------------------------------------------
// Access-token refresh — single-flighted to survive the backend's rotating
// refresh token. The backend revokes the old refresh-token jti on each
// /auth/refresh call, so two concurrent refreshes would race and the second
// would hit an already-revoked jti. The first 401 fires the refresh; the rest
// share its promise.
// ---------------------------------------------------------------------------

let refreshInFlight: Promise<string> | null = null;

/** Refresh the access token once. Concurrent callers share a single
 * `/auth/refresh` request. Resolves with the new access token; throws if no
 * refresh token is stored or the backend rejects. The refresh uses the
 * standalone `authFetch` (via `refreshTokenApi`), never the central client, so
 * it cannot recurse into 401 handling. */
export async function refreshAccessToken(): Promise<string> {
  if (refreshInFlight) return refreshInFlight;
  const rt = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!rt) throw new Error("no refresh token");
  refreshInFlight = (async () => {
    try {
      const res = await refreshTokenApi(rt);
      const { access_token, refresh_token } = res.data;
      useAuthStore.getState().setTokens(access_token, refresh_token);
      return access_token;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/** Authenticated fetch with one refresh+retry on 401 — the single entry point
 * shared by `request()` and the SSE pre-flights.
 *
 * - Non-401 responses are returned as-is for the caller to handle.
 * - On 401: refresh the access token (single-flighted), then retry the original
 *   request once with the new bearer. At most one retry — never loops.
 * - If refresh is unavailable (no refresh token stored) or fails, the ORIGINAL
 *   401 response is returned unchanged so the caller's unified handler
 *   (`throwApiErrorFromResponse` → `handle401` logout + a parsed ApiError carrying
 *   the server's own code/message) takes over. This keeps the pre-existing error
 *   contract intact instead of discarding the server's error envelope.
 *
 * The retry rebuilds headers from the original init plus the freshly-read
 * `authHeaders()` (now the new token), so `Authorization` is overridden while
 * `Accept`/`Content-Type`/etc. are preserved. */
export async function fetchWithAuthRetry(input: string, init: RequestInit): Promise<Response> {
  const res = await fetch(input, init);
  if (res.status !== 401) return res;
  try {
    await refreshAccessToken();
  } catch {
    return res;
  }
  const headers = {
    ...(init.headers as Record<string, string> | undefined),
    ...authHeaders(),
  };
  return fetch(input, { ...init, headers });
}

// ---------------------------------------------------------------------------
// Core request function
// ---------------------------------------------------------------------------

/** Extended options for the core request function. */
interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: BodyInit | null;
  /** If true, skip setting Content-Type (needed for FormData). */
  skipContentType?: boolean;
  /** If true, return a Blob instead of parsed JSON. */
  returnBlob?: boolean;
  /**
   * Per-call abort timeout in ms. Defaults to {@link DEFAULT_TIMEOUT_MS}.
   * Long-running endpoints (e.g. the synchronous AI chat reply) override this;
   * the default 10 s would abort them mid-flight.
   */
  timeoutMs?: number;
}

/**
 * Core fetch wrapper with timeout, token injection and unified error handling.
 *
 * - Timeout: aborts after 10 s by default.
 * - Auth: injects `Authorization: Bearer <token>` from localStorage when present.
 * - 401: deduplicates redirect to `/login`.
 * - 5xx / network: throws `ApiError` with descriptive message.
 * - `skipContentType`: skips Content-Type header so the browser can set the
 *   multipart boundary for FormData.
 * - `returnBlob`: returns `res.blob()` instead of `res.json()`.
 */
export async function request<T>(
  path: string,
  options: RequestOptions = {},
  prefix = API_PREFIX,
): Promise<T> {
  const { skipContentType = false, returnBlob = false, timeoutMs = DEFAULT_TIMEOUT_MS, ...init } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  const headers: Record<string, string> = {
    ...(skipContentType ? {} : { "Content-Type": "application/json" }),
    ...(init.headers as Record<string, string> | undefined),
    ...authHeaders(),
  };

  const method = (init.method as string) || "GET";
  const startedAt =
    typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();

  try {
    const res = await fetchWithAuthRetry(`${prefix}${path}`, {
      ...init,
      headers,
      signal: controller.signal,
    });

    const elapsedMs =
      typeof performance !== "undefined" && typeof performance.now === "function"
        ? Math.round(performance.now() - startedAt)
        : Math.round(Date.now() - startedAt);

    if (!res.ok) {
      await throwApiErrorFromResponse(res, {
        method,
        url: `${prefix}${path}`,
        durationMs: elapsedMs,
      });
    }

    if (import.meta.env.DEV) {
      console.debug("[jerry-api]", method, `${prefix}${path}`, res.status, {
        duration_ms: elapsedMs,
      });
    }

    if (returnBlob) {
      // Attach Content-Disposition so blob downloaders (e.g. ICS export) can
      // honour the server-provided filename. The attribute is non-enumerable
      // so it doesn't serialise into anything unexpected.
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition");
      if (cd) {
        Object.defineProperty(blob, "__contentDisposition", {
          value: cd,
          enumerable: false,
          configurable: true,
          writable: false,
        });
      }
      return blob as unknown as T;
    }
    // A bare 204 (or any explicitly empty body) has no JSON to parse —
    // res.json() throws a SyntaxError on an empty string. Every existing
    // jerry endpoint returns a `{success, data}` envelope even for deletes
    // (200), so this branch was previously unreached; the US-G05 memory
    // delete endpoint (contract-first, US-G05) is specified as a literal
    // 204, so callers expecting `void` need this to resolve cleanly instead
    // of throwing.
    if (res.status === 204) {
      return undefined as T;
    }
    return (await res.json()) as T;
  } catch (err: unknown) {
    if (err instanceof ApiError) throw err;
    if (err instanceof DOMException && err.name === "AbortError") {
      if (import.meta.env.DEV) {
        console.error("[jerry-api]", method, `${prefix}${path}`, "timeout", { timeout_ms: timeoutMs });
      }
      throw new ApiError(0, "Request timed out");
    }
    if (import.meta.env.DEV) {
      console.error("[jerry-api]", method, `${prefix}${path}`, "network_error", err instanceof Error ? err.message : String(err));
    }
    throw new ApiError(0, err instanceof Error ? err.message : "Network error");
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * FormData-aware fetch wrapper — same auth/timeout/error handling as `request()`,
 * but does NOT set Content-Type so the browser can set the multipart boundary.
 * Returns parsed JSON.
 */
function requestFormData<T>(path: string, options?: RequestInit, prefix = API_PREFIX): Promise<T> {
  return request<T>(path, { ...options, skipContentType: true }, prefix);
}

/**
 * FormData-aware fetch wrapper that returns a Blob (for file downloads).
 */
function requestFormDataBlob(path: string, options?: RequestInit, prefix = API_PREFIX): Promise<Blob> {
  return request<Blob>(path, { ...options, skipContentType: true, returnBlob: true }, prefix);
}

// ---------------------------------------------------------------------------
// Clients
// ---------------------------------------------------------------------------

export const clientsApi = {
  list: (params?: { search?: string; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set("search", params.search);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<Client>>(`/clients?${qs}`);
  },

  get: (id: string) => request<{ success: boolean; data: Client }>(`/clients/${id}`),

  create: (data: Partial<Client>) =>
    request<{ success: boolean; data: Client }>("/clients", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<Client>) =>
    request<{ success: boolean; data: Client }>(`/clients/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<{ success: boolean }>(`/clients/${id}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Matters
// ---------------------------------------------------------------------------

export const mattersApi = {
  list: (params?: {
    clientId?: string;
    status?: string;
    /** 事项名称/编号模糊检索（服务端 ilike） */
    search?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.clientId) qs.set("client_id", params.clientId);
    if (params?.status) qs.set("status", params.status);
    if (params?.search) qs.set("search", params.search);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<Matter>>(`/matters?${qs}`);
  },

  get: (id: string) => request<{ success: boolean; data: Matter }>(`/matters/${id}`),

  create: (data: Partial<Matter>) =>
    request<{ success: boolean; data: Matter }>("/matters", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<Matter>) =>
    request<{ success: boolean; data: Matter }>(`/matters/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  advanceStage: (id: string, stage: string) =>
    request<{ success: boolean; data: Matter }>(`/matters/${id}/stage`, {
      method: "POST",
      body: JSON.stringify({ stage }),
    }),

  listMembers: (matterId: string) =>
    request<{ success: boolean; data: Member[]; meta: { total: number; page: number; pageSize: number; totalPages: number } }>(`/matters/${matterId}/members`),

  addMember: (matterId: string, userId: string, role: string) =>
    request<{ success: boolean; data: Member }>(`/matters/${matterId}/members`, {
      method: "POST",
      body: JSON.stringify({ user_id: userId, role }),
    }),

  delete: (id: string) =>
    request<{ success: boolean }>(`/matters/${id}`, { method: "DELETE" }),

  removeMember: (matterId: string, memberId: string) =>
    request<{ success: boolean }>(`/matters/${matterId}/members/${memberId}`, { method: "DELETE" }),

  updateMember: (matterId: string, memberId: string, data: { role: string }) =>
    request<{ success: boolean; data: Member }>(`/matters/${matterId}/members/${memberId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  transferLead: (matterId: string, data: { successorUserId: string }) =>
    request<{ success: boolean; data: unknown }>(`/matters/${matterId}/transfer-lead`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  listStages: (matterId: string) =>
    request<{ success: boolean; data: MatterStageInstance[] }>(`/matters/${matterId}/stages`),

  activateStage: (matterId: string, stageId: string) =>
    request<{ success: boolean; data: MatterStageInstance }>(
      `/matters/${matterId}/stages/${stageId}/activate`,
      { method: "POST" },
    ),

  completeStage: (matterId: string, stageId: string) =>
    request<{ success: boolean; data: MatterStageInstance }>(
      `/matters/${matterId}/stages/${stageId}/complete`,
      { method: "POST" },
    ),

  /** 事项背景只读 (US-G04)。非成员 403（既有 check_matter_access 反枚举）。 */
  getBrief: (matterId: string) =>
    request<{ success: boolean; data: MatterBrief }>(`/matters/${matterId}/brief`),

  /** `version` 必须是最近一次 getBrief 读到的值——不符即 409 乐观锁冲突
   * (MATTER_BRIEF_VERSION_CONFLICT)；内容超 4000 字符即 422
   * (MATTER_BRIEF_TOO_LONG，details 携带 currentLength/maxLength)。 */
  updateBrief: (matterId: string, data: { content: string; version: number }) =>
    request<{ success: boolean; data: MatterBrief }>(`/matters/${matterId}/brief`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  /** 事项活动时间线 (AF3) — 聚合该事项自身 + 9 类子实体的审计事件。
   * 后端按 `created_at DESC` 排序分页返回 snake_case 字段；这里复用
   * `transformPaginated` 把数据字段递归转成 camelCase 以匹配
   * `AuditLogEntry` 类型。非成员/不存在的事项 → 403（反枚举）。*/
  matterTimeline: {
    list: (
      matterId: string,
      params?: { page?: number; pageSize?: number },
    ): Promise<{
      success: boolean;
      data: AuditLogEntry[];
      meta: { page: number; pageSize: number; total: number; totalPages: number };
    }> => {
      const qs = new URLSearchParams();
      if (params?.page) qs.set("page", String(params.page));
      if (params?.pageSize) qs.set("page_size", String(params.pageSize));
      return request<
        {
          success: boolean;
          data: AuditLogEntry[];
          meta: { page: number; pageSize: number; total: number; totalPages: number };
        }
      >(`/matters/${matterId}/timeline?${qs}`, undefined, API_PREFIX).then(
        transformPaginated,
      );
    },
  },
};

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

export const tasksApi = {
  list: (params?: { matterId?: string; meetingId?: string; status?: string; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.matterId) qs.set("matter_id", params.matterId);
    if (params?.meetingId) qs.set("meeting_id", params.meetingId);
    if (params?.status) qs.set("status", params.status);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<Task>>(`/tasks?${qs}`);
  },

  mine: (params?: { status?: string; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<Task>>(`/tasks/mine?${qs}`);
  },

  assigned: (params?: { status?: string; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<Task>>(`/tasks/assigned?${qs}`);
  },

  get: (id: string) => request<{ success: boolean; data: Task }>(`/tasks/${id}`),

  create: (data: Partial<Task>) =>
    request<{ success: boolean; data: Task }>("/tasks", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<Task>) =>
    request<{ success: boolean; data: Task }>(`/tasks/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  changeStatus: (id: string, status: string, waitingFor?: string) =>
    request<{ success: boolean; data: Task }>(`/tasks/${id}/status`, {
      method: "POST",
      body: JSON.stringify({ status, waiting_for: waitingFor }),
    }),

  delete: (id: string) =>
    request<{ success: boolean }>(`/tasks/${id}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

export const documentsApi = {
  list: (params?: {
    matterId?: string;
    sourceType?: string;
    search?: string;
    page?: number;
    pageSize?: number;
    scope?: string;
    ownerScope?: string;
    // US-D01~D06 (2026-07-04): extended filters for the rebuilt Document Center.
    groupByMatter?: boolean;
    tagSlugs?: string[];
    tagIds?: string[];
    tagOp?: "and" | "or";
    docTypes?: string[];
    actionStatuses?: string[];
    formats?: string[];
    dateField?: "created" | "updated";
    dateFrom?: string;
    dateTo?: string;
    viewId?: string;
  }) => {
    const qs = new URLSearchParams();
    if (params?.matterId) qs.set("matter_id", params.matterId);
    if (params?.sourceType) qs.set("source_type", params.sourceType);
    if (params?.search) qs.set("search", params.search);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    if (params?.scope) qs.set("scope", params.scope);
    if (params?.ownerScope) qs.set("owner_scope", params.ownerScope);
    // US-D01~D06 extended filters: backend takes CSV strings (Query(None) on
    // `str` only keeps the first value of a repeated key, so we must join).
    if (params?.groupByMatter) qs.set("group_by_matter", "true");
    if (params?.tagSlugs?.length) qs.set("tags", params.tagSlugs.join(","));
    if (params?.tagIds?.length) qs.set("tag_ids", params.tagIds.join(","));
    if (params?.tagOp) qs.set("tag_op", params.tagOp);
    if (params?.docTypes?.length) qs.set("doc_types", params.docTypes.join(","));
    if (params?.actionStatuses?.length) qs.set("action_statuses", params.actionStatuses.join(","));
    if (params?.formats?.length) qs.set("formats", params.formats.join(","));
    if (params?.dateField) qs.set("date_field", params.dateField);
    if (params?.dateFrom) qs.set("date_from", params.dateFrom);
    if (params?.dateTo) qs.set("date_to", params.dateTo);
    if (params?.viewId) qs.set("view_id", params.viewId);
    return request<PaginatedResponse<Document>>(`/documents?${qs}`);
  },

  get: (id: string) =>
    request<{ success: boolean; data: Document }>(`/documents/${id}`),

  /**
   * 按 AI 引擎的文档标识取文档。引用载荷里的文档 id 是引擎侧标识，与主库文档
   * id 不是同一套；「定位原文」需要先反查到主库文档才能打开查看器。
   */
  getByEngineId: (engineDocumentId: string) =>
    request<{ success: boolean; data: Document }>(
      `/documents/by-engine-id/${encodeURIComponent(engineDocumentId)}`,
    ),

  matterTree: (matterId: string, parentId?: string) => {
    const qs = new URLSearchParams();
    if (parentId) qs.set("parent_id", parentId);
    return request<{ success: boolean; data: Document[] }>(`/matters/${matterId}/tree?${qs}`);
  },

  update: (id: string, data: { title?: string; type?: string }) =>
    request<{ success: boolean; data: Document }>(`/documents/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  createFolder: (data: { matterId: string; title: string; parentId?: string }) =>
    request<{ success: boolean; data: Document }>("/documents/folder", {
      method: "POST",
      body: JSON.stringify({
        matter_id: data.matterId,
        title: data.title,
        parent_id: data.parentId ?? null,
      }),
    }),

  upload: async (data: { matterId: string; title: string; type: string; file: File }) => {
    const formData = new FormData();
    formData.append("file", data.file);
    formData.append("matter_id", data.matterId);
    formData.append("title", data.title);
    formData.append("type", data.type);

    const res = await fetchWithAuthRetry(`${API_PREFIX}/documents/upload`, {
      method: "POST",
      headers: authHeaders(),
      body: formData,
    });

    if (!res.ok) {
      await throwApiErrorFromResponse(res);
    }

    return res.json() as Promise<{ success: boolean; data: Document }>;
  },

  uploadWithScope: async (data: {
    file: File;
    title: string;
    type: string;
    ownerScope: DocOwnerScope;
    ownerScopeId?: string;
    matterId?: string;
  }) => {
    const formData = new FormData();
    formData.append("file", data.file);
    formData.append("title", data.title);
    formData.append("type", data.type);
    formData.append("owner_scope", data.ownerScope);
    if (data.ownerScopeId) formData.append("owner_scope_id", data.ownerScopeId);
    if (data.matterId) formData.append("matter_id", data.matterId);

    const res = await fetchWithAuthRetry(`${API_PREFIX}/documents/upload`, {
      method: "POST",
      headers: authHeaders(),
      body: formData,
    });

    if (!res.ok) {
      await throwApiErrorFromResponse(res);
    }

    return res.json() as Promise<{ success: boolean; data: Document }>;
  },

  delete: (id: string) =>
    request<{ success: boolean }>(`/documents/${id}`, { method: "DELETE" }),

  /** D3: rebuild a document's RAG index. Recovery path for DEGRADED / FAILED docs. */
  reindex: (id: string) =>
    request<{
      success: boolean;
      data: { id: string; rag_index_status: string };
    }>(`/documents/${id}/reindex`, { method: "POST" }),

  /** Compare two existing jerry documents via the jerry wrapper endpoint. */
  compareDocuments: (data: {
    sourceDocumentId: string;
    targetDocumentId: string;
    name?: string;
  }) =>
    request<{
      success: boolean;
      data: {
        comparisonId: string;
        veridocComparisonId: string;
        sourceDocumentId: string;
        targetDocumentId: string;
        status: string;
        changeCount: number | null;
        name: string | null;
      };
    }>(
      `/documents/${data.sourceDocumentId}/compare-against/${data.targetDocumentId}`,
      {
        method: "POST",
        body: JSON.stringify({ name: data.name ?? null }),
      },
    ).then((resp) => resp.data),

  /**
   * List the version chain of a document (US-C02).
   * GET /documents/{id}/versions → { success, data: { versions: Document[] } }
   * ordered by version desc. Each entry is a full Document row; the chain
   * shares a common `parent_version_id` lineage.
   *
   * The backend nests the array under `data.versions` (rather than returning
   * `data` as a bare array as US-C02's wire sketch suggested); unwrap here so
   * consumers see a flat `Document[]`.
   */
  versions: (documentId: string) =>
    request<{ success: boolean; data: { versions: Document[] } }>(
      `/documents/${documentId}/versions`,
    ).then((resp) => resp.data.versions ?? []),
};

// ---------------------------------------------------------------------------
// Redact — 一键脱敏 (Phase 2 Batch1 后端 / Batch2 前端)
//
//   POST /documents/{id}/redact              trigger (async, returns status)
//   GET  /documents/{id}/redact-status       poll until ready/failed
//   GET  /documents/{id}/download-redacted   blob download (status=ready)
//
// trigger/status unwrap `data` for callers; download-redacted reuses the
// shared `downloadFromUrl` blob pipeline via the redact-specific wrapper in
// utils/download.
// ---------------------------------------------------------------------------

export const redactApi = {
  /** POST /documents/{id}/redact — trigger async redaction. */
  trigger: (documentId: string, types: RedactType[]) =>
    request<{ success: boolean; data: RedactStatusResponse }>(
      `/documents/${documentId}/redact`,
      {
        method: "POST",
        body: JSON.stringify({ types }),
      },
    ).then((res) => res.data),

  /** GET /documents/{id}/redact-status — current status (poll). */
  status: (documentId: string) =>
    request<{ success: boolean; data: RedactStatusResponse }>(
      `/documents/${documentId}/redact-status`,
    ).then((res) => res.data),
};

// ---------------------------------------------------------------------------
// Workbench — Document Center tree / views / tags (US-D01~D06)
//
// These endpoints back the rebuilt Document Center: the left matter tree, the
// tag dictionary + per-document tag edits, and the saved-view CRUD. They reuse
// the shared `request<T>()` wrapper so 401 redirects, timeouts, and `ApiError`
// behave consistently with the rest of the API surface.
// ---------------------------------------------------------------------------

export const workbenchApi = {
  /** Left-sidebar matters tree (client → matter, with doc counts). */
  mattersTree: () =>
    request<{ success: boolean; data: MattersTreeNode[] }>(
      "/workbench/matters-tree",
    ).then((res) => res.data),

  /** Tag dictionary (system presets + tenant customs). */
  listTags: (search?: string) => {
    const qs = new URLSearchParams();
    if (search) qs.set("search", search);
    return request<{
      success: boolean;
      data: Array<{ id: string; name: string; slug: string; isSystem: boolean }>;
    }>(`/tags${qs.toString() ? `?${qs}` : ""}`).then((res) => res.data);
  },

  /** Replace a document's tag set (add/remove, source='user'). */
  updateDocumentTags: (documentId: string, tagSlugs: string[]) =>
    request<{ success: boolean; data: unknown }>(
      `/documents/${documentId}/tags`,
      {
        method: "PUT",
        body: JSON.stringify({ tags: tagSlugs, source: "user" }),
      },
    ).then((res) => res.data),

  /** List the current user's saved workbench views. */
  listViews: () =>
    request<{ success: boolean; data: WorkbenchView[] }>(
      "/workbench/views",
    ).then((res) => res.data),

  /** Save current filter/column config as a new view. */
  createView: (args: {
    name: string;
    config: WorkbenchViewConfig;
    isDefault?: boolean;
  }) =>
    request<{ success: boolean; data: WorkbenchView }>(`/workbench/views`, {
      method: "POST",
      body: JSON.stringify({
        name: args.name,
        config: args.config,
        is_default: args.isDefault ?? false,
      }),
    }).then((res) => res.data),

  /** Update a saved view's name/config/default flag. */
  updateView: (
    viewId: string,
    args: {
      name?: string;
      config?: WorkbenchViewConfig;
      isDefault?: boolean;
    },
  ) =>
    request<{ success: boolean; data: WorkbenchView }>(
      `/workbench/views/${viewId}`,
      {
        method: "PUT",
        body: JSON.stringify({
          name: args.name,
          config: args.config,
          is_default: args.isDefault,
        }),
      },
    ).then((res) => res.data),

  /** Delete a saved view. */
  deleteView: (viewId: string) =>
    request<{ success: boolean; data: null }>(
      `/workbench/views/${viewId}`,
      { method: "DELETE" },
    ),

  /**
   * Batch action on multiple documents (US-D07 — reserved). The endpoint is
   * not yet shipped on the backend; the call will return a 404 `ApiError` until
   * it lands, which is the intended signal (rather than a silent no-op).
   */
  batchDocuments: (args: {
    ids: string[];
    action: string;
    payload?: Record<string, unknown>;
  }) =>
    request<{ success: boolean; data: unknown }>(`/documents/batch`, {
      method: "POST",
      body: JSON.stringify(args),
    }).then((res) => res.data),
};

// ---------------------------------------------------------------------------
// Conversations
// ---------------------------------------------------------------------------

export const conversationsApi = {
  list: (params?: {
    mode?: string;
    page?: number;
    pageSize?: number;
    /** W1-4: "archived" lists the archive view; omitted/"active" is the default. */
    status?: "active" | "archived";
    /** Batch 3: filter by panel anchor type (e.g., "workbench_review"). */
    anchorType?: string;
    /** Batch 3: filter by panel anchor id (the entity the conversation is bound to). */
    anchorId?: string;
  }) => {
    const qs = new URLSearchParams();
    if (params?.mode) qs.set("mode", params.mode);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    if (params?.status) qs.set("status", params.status);
    if (params?.anchorType) qs.set("anchor_type", params.anchorType);
    if (params?.anchorId) qs.set("anchor_id", params.anchorId);
    return request<PaginatedResponse<Conversation>>(`/conversations?${qs}`);
  },

  /**
   * 按关键词搜索当前用户可见的会话（W2-5）。命中范围 = 标题 + 消息内容，
   * `status` 语义与 `list` 一致（默认 active，显式 archived 搜归档视图，不
   * 跨状态混搜）。`q` 为空由调用方（`useSearchConversations` 的 `enabled`
   * 门控）保证不发起请求 —— 后端对空白关键词返回 422。
   */
  search: (params: {
    q: string;
    status?: "active" | "archived";
    page?: number;
    pageSize?: number;
  }) => {
    const qs = new URLSearchParams();
    qs.set("q", params.q);
    if (params.status) qs.set("status", params.status);
    if (params.page) qs.set("page", String(params.page));
    if (params.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<ConversationSearchResult>>(
      `/conversations/search?${qs}`,
    );
  },

  get: (id: string) => request<{ success: boolean; data: Conversation }>(`/conversations/${id}`),

  /** Documents this conversation is associated with, grouped by origin (uploaded
   * / selected / both) plus the implicit corpus count when no explicit selection
   * is set. Read-only — for the conversation documents panel. */
  listDocuments: (id: string) =>
    request<{ success: boolean; data: ConversationDocumentsScope }>(
      `/conversations/${id}/documents`,
    ),

  create: (data: {
    title?: string;
    matterId?: string;
    taskId?: string;
    mode?: string;
    contextDocumentIds?: string[];
    /** Batch 3: panel anchor type (e.g., "workbench_review"). */
    anchorType?: string;
    /** Batch 3: panel anchor id (the entity the conversation is bound to). */
    anchorId?: string;
  }) =>
    request<{ success: boolean; data: Conversation }>("/conversations", {
      method: "POST",
      body: JSON.stringify({
        title: data.title,
        matter_id: data.matterId,
        task_id: data.taskId,
        mode: data.mode,
        // Explicit document-library retrieval scope (W5); omitted when empty so
        // the backend derives the implicit matter/personal corpus.
        ...(data.contextDocumentIds && data.contextDocumentIds.length > 0
          ? { context_document_ids: data.contextDocumentIds }
          : {}),
        // Batch 3: panel anchor fields (snake_case for backend).
        ...(data.anchorType ? { anchor_type: data.anchorType } : {}),
        ...(data.anchorId ? { anchor_id: data.anchorId } : {}),
      }),
    }),

  /** Delete a conversation (owner-only; a 404 anti-enumeration guard hides
   * others' ids). Used for best-effort cleanup of an abandoned draft (W6). */
  remove: (id: string) =>
    request<{ success: boolean }>(`/conversations/${id}`, { method: "DELETE" }),

  update: (
    id: string,
    data: {
      title?: string;
      matterId?: string;
      /** W1-4: "archived" hides from the default listing; "active" restores. */
      status?: "active" | "archived";
    },
  ) =>
    request<{ success: boolean; data: Conversation }>(`/conversations/${id}`, {
      method: "PATCH",
      body: JSON.stringify({
        title: data.title,
        matter_id: data.matterId,
        ...(data.status ? { status: data.status } : {}),
      }),
    }),

  linkMatter: (id: string, matterId: string) =>
    request<{ success: boolean; data: Conversation }>(`/conversations/${id}/link-matter`, {
      method: "POST",
      body: JSON.stringify({ matter_id: matterId }),
    }),

  /** Pin (or clear, `agentName: null`) this conversation's manually-specified
   * "专业顾问" (US-G01). Owner-only; a non-null name must be one of the
   * caller's visible advisors (GET /advisors) or the backend rejects 404. */
  updateAdvisor: (id: string, agentName: string | null) =>
    request<{ success: boolean; data: Conversation }>(`/conversations/${id}/advisor`, {
      method: "PATCH",
      body: JSON.stringify({ agentName }),
    }),

  listMessages: (
    id: string,
    params?: { page?: number; pageSize?: number; order?: "asc" | "desc" },
  ) => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    if (params?.order) qs.set("order", params.order);
    return request<PaginatedResponse<Message>>(`/conversations/${id}/messages?${qs}`);
  },

  /**
   * Upload a document as a conversation attachment. The file enters the document
   * library and becomes conversation context. Routed through the shared request
   * wrapper (not a bare fetch) so an ApiError carries `code`
   * (CHAT_ATTACHMENT_UNSUPPORTED_TYPE) for the caller to branch on.
   */
  uploadAttachment: (conversationId: string, file: File, title?: string) => {
    const formData = new FormData();
    formData.append("file", file);
    if (title) formData.append("title", title);
    return request<{ success: boolean; data: Document }>(
      `/conversations/${conversationId}/attachments`,
      {
        method: "POST",
        body: formData,
        skipContentType: true,
        // A document upload can be larger/slower than a plain JSON call; the
        // 10 s default is too tight. The engine indexes asynchronously, so this
        // only covers the transfer + registration, not indexing.
        timeoutMs: 120_000,
      },
    );
  },

  /**
   * 批 5：一条助手轮上落库的法律能力执行结果（结果卡数据源，只读）。数据
   * 来自 `jerry_capability_run`——结果卡不读聊天 SSE 流里转瞬即逝的 digest。
   * `input_required`（缺参数追问）不落库，故不会出现在返回列表里。
   */
  listCapabilityRuns: (conversationId: string, messageId: string) =>
    request<{ success: boolean; data: CapabilityRun[] }>(
      `/conversations/${conversationId}/messages/${messageId}/capability-runs`,
    ),

  /**
   * 对一条 AI 回复表态：认同 / 不认同 / 撤销表态（表态设计 阶段二）。
   *
   * 表态不驱动任何写动作，只是一条意见记录。否决一次点击即完成——`reasonCode`
   * 与 `reasonText` 都是选填，且仅在 `verdict === "rejected"` 时可给；服务端
   * 对「采纳却带否决理由」直接报错而不是静默丢弃。
   *
   * `excerpt` 是律师在正文里选中的那一句（纯文本快照，选填）；不选中时表态
   * 覆盖整条回答。正文快照由服务端从库里的消息行现取，不由前端提交。
   *
   * 服务端 append-only：每次调用追加一行（含撤销），返回刚落下的那一行。
   */
  createVerdict: (
    conversationId: string,
    messageId: string,
    body: {
      verdict: AiFindingProcessed;
      reasonCode?: AiVerdictReasonCode | null;
      reasonText?: string | null;
      excerpt?: string | null;
    },
  ) =>
    request<{ success: boolean; data: AiVerdict }>(
      `/conversations/${conversationId}/messages/${messageId}/verdict`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  /** 本人在该会话内的当前表态（每条消息最新一行），进会话时一次性回填——
   *  逐条消息各发一次请求会让长对话开出几十个并发查询。 */
  listVerdicts: (conversationId: string) =>
    request<{ success: boolean; data: AiVerdict[] }>(
      `/conversations/${conversationId}/verdicts`,
    ),

  /**
   * 对一条会话侧法律能力发现表态（表态设计已定结论第 7 条）。
   *
   * 这批发现已经是 `jerry_ai_finding` 的行，因此走行内三态、与文档审查侧共用
   * 同一套语义；此前它们在结果卡上是纯只读的，律师对着错误条目无处表态。
   */
  updateCapabilityFindingVerdict: (
    conversationId: string,
    messageId: string,
    capabilityRunId: string,
    findingId: string,
    body: {
      processed: AiFindingProcessed;
      reasonCode?: AiVerdictReasonCode | null;
      reasonText?: string | null;
    },
  ) =>
    request<{ success: boolean; data: CapabilityFindingVerdict }>(
      `/conversations/${conversationId}/messages/${messageId}` +
        `/capability-runs/${capabilityRunId}/ai-findings/${findingId}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),

  /**
   * 「停止本轮」（能力隐式调用架构 §6 决策 2）：只停止内联等待，不取消引擎
   * 侧的后台任务——任务仍会跑完，结果稍后可在任务列表查看。本会话此刻并未
   * 在等待任何异步能力时是无操作（`stopped: false`），不是报错。
   */
  stop: (id: string) =>
    request<{ success: boolean; data: { stopped: boolean } }>(`/conversations/${id}/stop`, {
      method: "POST",
    }),
};

// ---------------------------------------------------------------------------
// Advisors (US-G01/US-G02 "专业顾问" directory)
// ---------------------------------------------------------------------------

export const advisorsApi = {
  /** Visible advisor directory = builtin engine agents ∪ the caller's visible,
   * bound agent-type capability cards. Shared source for the message-level
   * advisor badge, the advisor picker, and (server-side) auto-dispatch. */
  list: () =>
    request<{ success: boolean; data: { items: AdvisorItem[]; engineAvailable: boolean } }>(
      "/advisors",
    ),
};

// ---------------------------------------------------------------------------
// Client Service Team
// ---------------------------------------------------------------------------

export interface ClientServiceTeamMember {
  id: string;
  user_id: string;
  name: string;
  email: string | null;
  role: string;
  grade_name: string | null;
}

export const clientServiceTeamApi = {
  list: (clientId: string) =>
    request<{ success: boolean; data: ClientServiceTeamMember[] }>(
      `/clients/${clientId}/service-team`,
    ),
};

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export const dashboardApi = {
  compass: () => request<{ success: boolean; data: CompassData }>("/dashboard/compass"),
};

// ---------------------------------------------------------------------------
// Deadlines
// ---------------------------------------------------------------------------

export const deadlinesApi = {
  list: (params?: { matterId?: string; status?: string; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.matterId) qs.set("matter_id", params.matterId);
    if (params?.status) qs.set("status", params.status);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<Deadline>>(`/deadlines?${qs}`);
  },

  get: (id: string) => request<{ success: boolean; data: Deadline }>(`/deadlines/${id}`),

  create: (data: { matterId?: string | null; title: string; description?: string; dueDate: string; isStatutory?: boolean; warningDays?: number }) =>
    request<{ success: boolean; data: Deadline }>("/deadlines", {
      method: "POST",
      body: JSON.stringify({
        matter_id: data.matterId ?? null,
        title: data.title,
        description: data.description,
        due_date: data.dueDate,
        is_statutory: data.isStatutory ?? false,
        warning_days: data.warningDays ?? 3,
      }),
    }),

  update: (id: string, data: { title?: string; description?: string; dueDate?: string; status?: string; isStatutory?: boolean; warningDays?: number }) =>
    request<{ success: boolean; data: Deadline }>(`/deadlines/${id}`, {
      method: "PATCH",
      body: JSON.stringify({
        title: data.title,
        description: data.description,
        due_date: data.dueDate,
        status: data.status,
        is_statutory: data.isStatutory,
        warning_days: data.warningDays,
      }),
    }),

  delete: (id: string) =>
    request<{ success: boolean }>(`/deadlines/${id}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Time Entries
// ---------------------------------------------------------------------------

export const timeEntriesApi = {
  /** List time entries with v2 filters. `employeeId` filters by the tracked
   * user (not the operator). `status` is the run state (running/stopped);
   * `billingStatus` is the billing dimension (billable/non_billable/billed) —
   * the two are independent. */
  list: (params?: {
    matterId?: string;
    employeeId?: string;
    actionTypeId?: string;
    status?: "running" | "stopped";
    billingStatus?: "billable" | "non_billable" | "billed";
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.matterId) qs.set("matter_id", params.matterId);
    if (params?.employeeId) qs.set("employee_id", params.employeeId);
    if (params?.actionTypeId) qs.set("action_type_id", params.actionTypeId);
    if (params?.status) qs.set("status", params.status);
    if (params?.billingStatus) qs.set("billing_status", params.billingStatus);
    if (params?.dateFrom) qs.set("date_from", params.dateFrom);
    if (params?.dateTo) qs.set("date_to", params.dateTo);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<TimeEntry>>(`/time-entries?${qs}`);
  },

  /** Create a time entry.
   * - Timer mode: omit startTime and endTime → server stamps now.
   * - Manual mode: provide both startTime and endTime.
   * Prohibiting `startTime=null & endTime=Y` is enforced by TS types
   * (both are `string | null`, but FormDrawer guards at submit). */
  create: (data: TimeEntryCreatePayload) =>
    request<{ success: boolean; data: TimeEntry }>("/time-entries", {
      method: "POST",
      body: JSON.stringify({
        matter_id: data.matterId ?? null,
        task_id: data.taskId ?? null,
        employee_id: data.employeeId,
        action_type_id: data.actionTypeId ?? null,
        description: data.description,
        billing_status: data.billingStatus ?? "billable",
        is_private: data.isPrivate ?? false,
        hourly_rate: data.hourlyRate ?? null,
        start_time: data.startTime ?? null,
        end_time: data.endTime ?? null,
      }),
    }),

  stop: (id: string) =>
    request<{ success: boolean; data: TimeEntry }>(`/time-entries/${id}/stop`, {
      method: "POST",
    }),

  update: (id: string, data: TimeEntryUpdatePayload) =>
    request<{ success: boolean; data: TimeEntry }>(`/time-entries/${id}`, {
      method: "PATCH",
      body: JSON.stringify({
        matter_id: data.matterId,
        task_id: data.taskId,
        action_type_id: data.actionTypeId,
        description: data.description,
        billing_status: data.billingStatus,
        is_private: data.isPrivate,
        hourly_rate: data.hourlyRate,
      }),
    }),

  delete: (id: string) =>
    request<{ success: boolean }>(`/time-entries/${id}`, { method: "DELETE" }),

  /** Aggregated totals. matterId/employeeId/dateFrom/dateTo are all optional —
   * omit all four for a tenant-wide summary. */
  summary: (params?: {
    matterId?: string;
    employeeId?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => {
    const qs = new URLSearchParams();
    if (params?.matterId) qs.set("matter_id", params.matterId);
    if (params?.employeeId) qs.set("employee_id", params.employeeId);
    if (params?.dateFrom) qs.set("date_from", params.dateFrom);
    if (params?.dateTo) qs.set("date_to", params.dateTo);
    return request<{ success: boolean; data: TimeEntrySummary }>(
      `/time-entries/reports/summary?${qs}`,
    );
  },
};

// ---------------------------------------------------------------------------
// Action Types (time-entry categories)
// ---------------------------------------------------------------------------

export const actionTypesApi = {
  list: (params?: { isActive?: boolean; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.isActive !== undefined) qs.set("is_active", String(params.isActive));
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<ActionType>>(`/action-types?${qs}`);
  },
};

// ---------------------------------------------------------------------------
// Staff Profiles (tenant-scoped employee attributes)
// ---------------------------------------------------------------------------

export const staffProfilesApi = {
  me: () =>
    request<{ success: boolean; data: StaffProfile }>(`/staff-profiles/me`),
  list: (params?: { page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<StaffProfile>>(`/staff-profiles?${qs}`);
  },
};

// ---------------------------------------------------------------------------
// Resources (Knowledge Assets)
// ---------------------------------------------------------------------------

export const resourcesApi = {
  list: (params?: { type?: string; category?: string; scope?: string; search?: string; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.type) qs.set("type", params.type);
    if (params?.category) qs.set("category", params.category);
    if (params?.scope) qs.set("scope", params.scope);
    if (params?.search) qs.set("search", params.search);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<Resource>>(`/resources?${qs}`);
  },

  get: (id: string) => request<{ success: boolean; data: Resource }>(`/resources/${id}`),

  create: (data: { title: string; type: string; description?: string; category?: string; content?: string; tags?: string[]; scope?: string; scopeRef?: string }) =>
    request<{ success: boolean; data: Resource }>("/resources", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: { title?: string; description?: string; category?: string; content?: string; tags?: string[]; scope?: string; scopeRef?: string; isActive?: boolean }) =>
    request<{ success: boolean; data: Resource }>(`/resources/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<{ success: boolean }>(`/resources/${id}`, { method: "DELETE" }),
};


// ---------------------------------------------------------------------------
// Platform Users (auth module)
// ---------------------------------------------------------------------------

export const usersApi = {
  list: (params?: { search?: string; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set("search", params.search);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<User>>(`/users?${qs}`, undefined, AUTH_PREFIX);
  },
  /** Admin-only: create a new tenant user. Backed by POST /auth/register. */
  create: (payload: UserCreatePayload) =>
    request<{ success: boolean; data: User }>(
      "/register",
      {
        method: "POST",
        body: JSON.stringify({
          email: payload.email,
          password: payload.password,
          name: payload.name,
          phone: payload.phone ?? null,
        }),
      },
      AUTH_PREFIX,
    ),
  /** Admin-only: update name/email/is_active. Backed by PATCH /auth/users/{id}. */
  update: (userId: string, payload: UserUpdatePayload) =>
    request<{ success: boolean; data: User }>(
      `/users/${userId}`,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
      AUTH_PREFIX,
    ),
  /** Admin-only: soft-delete account. Backed by DELETE /auth/users/{id}. */
  remove: (userId: string) =>
    request<{ success: boolean; data: null }>(
      `/users/${userId}`,
      { method: "DELETE" },
      AUTH_PREFIX,
    ),
};

/**
 * Lawyers — tenant user roster with grade info.
 *
 * Backed by `GET /api/v1/jerry/lawyers` (any authenticated user; no admin
 * role required). Used by `useUsers` as a 403 fallback so non-admin roles
 * (partner, associate, etc.) can still render user names in matter team
 * panels, assignee dropdowns, and task forms.
 */
export const lawyersApi = {
  list: (params?: {
    search?: string;
    gradeCode?: string;
    departmentId?: string;
    active?: boolean;
    page?: number;
    pageSize?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set("search", params.search);
    if (params?.gradeCode) qs.set("grade_code", params.gradeCode);
    if (params?.departmentId) qs.set("department_id", params.departmentId);
    if (params?.active !== undefined) qs.set("active", String(params.active));
    if (params?.page) qs.set("page", String(params.page));
    // Backend PaginationParams reads `page_size`; sending `pageSize` was silently
    // ignored, capping the roster (and every assignee dropdown) at the default 20.
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<Lawyer>>(`/lawyers?${qs}`, undefined, API_PREFIX);
  },
};

// ---------------------------------------------------------------------------
// Audit Logs
// ---------------------------------------------------------------------------

export interface AuditLogEntry {
  id: string;
  action: string;
  resourceType: string;
  resourceId: string;
  actor: string | null;
  userId: string | null;
  changes: Record<string, { old: unknown; new: unknown }> | null;
  requestId: string;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Employee management — staff profiles, grades, departments, positions, roles
//
// Backed by jerry (/staff-profiles, /grades), org (/departments, /positions),
// and rbac (/users/{id}/roles) modules. Used by the employee roster page.
// ---------------------------------------------------------------------------

const ORG_PREFIX = "/api/v1/org";
const RBAC_PREFIX = "/api/v1/rbac";

export const staffApi = {
  /** List staff profiles (admin/passthrough). */
  list: (params?: { page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("pageSize", String(params.pageSize));
    return request<PaginatedResponse<Lawyer>>(`/staff-profiles?${qs}`, undefined, API_PREFIX);
  },
  /** Admin-only: attach a staff profile (grade + employee number) to a user. */
  create: (payload: StaffProfileCreatePayload) =>
    request<{ success: boolean; data: { id: string } }>(
      "/staff-profiles",
      { method: "POST", body: JSON.stringify(payload) },
      API_PREFIX,
    ),
  /** Admin-only: update grade/employee_number. */
  update: (profileId: string, payload: StaffProfileUpdatePayload) =>
    request<{ success: boolean; data: unknown }>(
      `/staff-profiles/${profileId}`,
      { method: "PATCH", body: JSON.stringify(payload) },
      API_PREFIX,
    ),
  /** Admin-only: soft-delete a staff profile (US-H02). */
  remove: (profileId: string) =>
    request<{ success: boolean; data: null }>(
      `/staff-profiles/${profileId}`,
      { method: "DELETE" },
      API_PREFIX,
    ),
};

export const gradesApi = {
  list: (params?: { page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("pageSize", String(params.pageSize));
    return request<PaginatedResponse<EmployeeGrade>>(`/grades?${qs}`, undefined, API_PREFIX);
  },
};

export const departmentsApi = {
  /** Tree of departments for the current tenant. */
  tree: () =>
    request<{ success: boolean; data: Department[] }>(
      "/departments/tree",
      undefined,
      ORG_PREFIX,
    ),
  /** Members of a single department. */
  listMembers: (departmentId: string) =>
    request<{ success: boolean; data: unknown[] }>(
      `/departments/${departmentId}/members`,
      undefined,
      ORG_PREFIX,
    ),
  /** Add a user to a department. */
  addMember: (departmentId: string, payload: DepartmentMemberAddPayload) =>
    request<{ success: boolean; data: unknown }>(
      `/departments/${departmentId}/members`,
      { method: "POST", body: JSON.stringify(payload) },
      ORG_PREFIX,
    ),
  /** Remove a user from a department. */
  removeMember: (departmentId: string, userId: string) =>
    request<{ success: boolean; data: null }>(
      `/departments/${departmentId}/members/${userId}`,
      { method: "DELETE" },
      ORG_PREFIX,
    ),
};

export const positionsApi = {
  list: () =>
    request<{ success: boolean; data: Position[] }>("/positions", undefined, ORG_PREFIX),
};

export const rolesApi = {
  list: () =>
    request<{ success: boolean; data: Role[] }>("/roles", undefined, RBAC_PREFIX),
};

export const userRolesApi = {
  /** Replace a user's role set with the given role IDs (PUT semantics). */
  assign: (userId: string, roleIds: string[]) =>
    request<{ success: boolean; data: unknown }>(
      `/users/${userId}/roles`,
      { method: "PUT", body: JSON.stringify({ role_ids: roleIds }) },
      RBAC_PREFIX,
    ),
  /** Remove a single role from a user. */
  remove: (userId: string, roleId: string) =>
    request<{ success: boolean; data: null }>(
      `/users/${userId}/roles/${roleId}`,
      { method: "DELETE" },
      RBAC_PREFIX,
    ),
};

export const auditLogsApi = {
  list: (params?: {
    resourceType?: string;
    resourceId?: string;
    action?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.action) qs.set("action", params.action);
    if (params?.limit) qs.set("limit", String(params.limit));
    return request<{ success: boolean; data: AuditLogEntry[] }>(
      `?${qs}`,
      undefined,
      AUDIT_PREFIX,
    );
  },

  forResource: async (resourceType: string, resourceId: string, limit = 20) => {
    const res = await request<{ success: boolean; data: AuditLogEntry[] }>(
      `/${resourceType}/${resourceId}?page_size=${limit}`,
      undefined,
      AUDIT_PREFIX,
    );
    return { success: res.success, data: toCamelCase(res.data) };
  },
};

// ---------------------------------------------------------------------------
// Auth — current user profile
// ---------------------------------------------------------------------------

export const authApi = {
  me: () =>
    request<{ success: boolean; data: User & { tenantName?: string } }>(
      "/me",
      undefined,
      AUTH_PREFIX,
    ),

  updateMe: (payload: { name?: string; phone?: string }) =>
    request<{ success: boolean; data: User }>(
      "/me",
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
      AUTH_PREFIX,
    ),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ success: boolean; message: string }>(
      "/change-password",
      {
        method: "POST",
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      },
      AUTH_PREFIX,
    ),
};

// ---------------------------------------------------------------------------
// System LLM settings (admin only)
//
// Backed by `/api/v1/jerry/settings/llm*`, which proxies the Lobster AI engine.
// The engine holds a single process-global config, so this is a firm-wide
// admin setting. The API key is only ever returned masked; send `apiKey` only
// to rotate it (omit to keep the stored key).
// ---------------------------------------------------------------------------

export interface LLMSettings {
  provider: string;
  baseUrl: string;
  model: string;
  apiKeyMasked: string;
}

export interface LLMPreset {
  providerId: string;
  name: string;
  baseUrl: string;
  models: string[];
  apiKeyEnv: string;
  requiresBaseUrl: boolean;
  docsUrl: string;
}

export interface LLMTestResult {
  success: boolean;
  message: string;
}

export interface LLMSettingsUpdatePayload {
  provider: string;
  baseUrl: string;
  model: string;
  apiKey?: string;
}

export const llmSettingsApi = {
  get: () => request<{ success: boolean; data: LLMSettings }>("/settings/llm"),

  presets: () =>
    request<{ success: boolean; data: LLMPreset[] }>("/settings/llm/presets"),

  update: (payload: LLMSettingsUpdatePayload) =>
    request<{ success: boolean; data: LLMSettings }>("/settings/llm", {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  test: (payload: LLMSettingsUpdatePayload) =>
    request<{ success: boolean; data: LLMTestResult }>("/settings/llm/test", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
};

// ---------------------------------------------------------------------------
// Capabilities (AI Capabilities)
// ---------------------------------------------------------------------------

export const capabilitiesApi = {
  list: async (params?: {
    type?: string;
    scope?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  }) => {
    // Backend caps page_size at 100 (config: pagination_max_page_size).
    const pageSize = Math.min(params?.pageSize ?? 100, 100);

    const baseQs = new URLSearchParams();
    if (params?.type) baseQs.set("type", params.type);
    if (params?.scope) baseQs.set("scope", params.scope);
    if (params?.search) baseQs.set("search", params.search);
    baseQs.set("page_size", String(pageSize));

    const fetchPage = async (page: number) => {
      const qs = new URLSearchParams(baseQs);
      qs.set("page", String(page));
      const raw = await request<PaginatedResponse<Capability>>(`/capabilities?${qs}`);
      return transformPaginated(raw);
    };

    // Explicit page → single-page mode (preserves caller-controlled paging).
    if (params?.page !== undefined) {
      return fetchPage(params.page);
    }

    // Default: auto-paginate the whole set. The CapabilitiesPanel filters by
    // type/practice-area/source on the client, so it needs every capability.
    // 164 seeded mcp_tools (newest created_at) otherwise push the agent/skill/
    // playbook cards past the first page, hiding them from every tab.
    const first = await fetchPage(1);
    if (first.data.length >= first.meta.total) {
      return first;
    }
    const all = [...first.data];
    for (let page = 2; page <= first.meta.totalPages; page++) {
      all.push(...(await fetchPage(page)).data);
    }
    return { ...first, data: all };
  },

  get: async (id: string) => {
    const raw = await request<{ success: boolean; data: Capability }>(`/capabilities/${id}`);
    return { ...raw, data: toCamelCase(raw.data) };
  },

  create: async (data: CapabilityCreateRequest) => {
    const raw = await request<{ success: boolean; data: Capability }>("/capabilities", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return { ...raw, data: toCamelCase(raw.data) };
  },

  update: async (id: string, data: CapabilityUpdateRequest) => {
    const raw = await request<{ success: boolean; data: Capability }>(`/capabilities/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    return { ...raw, data: toCamelCase(raw.data) };
  },

  delete: (id: string) =>
    request<{ success: boolean }>(`/capabilities/${id}`, { method: "DELETE" }),

  toggleActive: (id: string) =>
    request<{ success: boolean; data: { id: string; isActive: boolean } }>(
      `/capabilities/${id}/toggle-active`,
      { method: "POST" },
    ),

  toggleStar: (id: string) =>
    request<{ success: boolean; data: { id: string; isStarred: boolean } }>(
      `/capabilities/${id}/toggle-star`,
      { method: "POST" },
    ),

  /** 任务二阶段二：可绑定引擎目标目录（GET /engine/catalog）——builtin agent
   *  名 + 激活 skill id，创建/编辑弹窗「绑定引擎目标」选择器的数据源。
   *  agents[].name / skills[].id 即写入 engineRefName 的绑定值。引擎不可达
   *  时后端返回 502 ENGINE_CATALOG_UNAVAILABLE —— 选择器显式报错，不给
   *  静默空目录。 */
  getEngineCatalog: async () => {
    const raw = await request<{ success: boolean; data: EngineCatalog }>(
      "/engine/catalog",
    );
    return { ...raw, data: toCamelCase(raw.data) as EngineCatalog };
  },
};

// ---------------------------------------------------------------------------
// Custom Advisors (US-G03 律所自定义顾问)
// ---------------------------------------------------------------------------

export const customAdvisorsApi = {
  /** Engine registration runs before any local write (BR-G03-3 原子性) — a
   * rejected/unreachable engine call (409 重名 / 422 工具越界或注册失败 /
   * 403 scope 越权) leaves no card behind. */
  create: (data: CustomAdvisorCreateRequest) =>
    request<{ success: boolean; data: CustomAdvisor }>("/custom-advisors", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  /** Full detail incl. systemPrompt/tools — manage-access only; a 403 means
   * the caller may not edit this advisor (S3-15 invoke ≠ inspect). */
  get: (id: string) =>
    request<{ success: boolean; data: CustomAdvisor }>(`/custom-advisors/${id}`),

  update: (id: string, data: CustomAdvisorUpdateRequest) =>
    request<{ success: boolean; data: CustomAdvisor }>(`/custom-advisors/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  /** Uninstalls the engine addon + soft-deletes the card; idempotent. */
  delete: (id: string) =>
    request<{ success: boolean; data: null }>(`/custom-advisors/${id}`, {
      method: "DELETE",
    }),
};

// ---------------------------------------------------------------------------
// MCP Servers (AI capability providers)
// ---------------------------------------------------------------------------

export const mcpServersApi = {
  list: async () => {
    const raw = await request<{ success: boolean; data: McpServer[] }>(
      "/mcp-servers",
    );
    return { ...raw, data: raw.data.map((s) => toCamelCase(s) as McpServer) };
  },

  create: async (data: McpServerRegisterRequest) => {
    // Backend reads snake_case; serialize explicitly (env/headers keys are
    // caller-controlled, not transformed).
    const body = {
      name: data.name,
      transport_type: data.transport_type,
      command: data.command,
      url: data.url,
      env: data.env,
      headers: data.headers,
      timeout_seconds: data.timeout_seconds,
      auto_sync: data.auto_sync,
    };
    const raw = await request<{ success: boolean; data: McpServer }>(
      "/mcp-servers",
      { method: "POST", body: JSON.stringify(body) },
    );
    return { ...raw, data: toCamelCase(raw.data) as McpServer };
  },

  sync: async (id: string) => {
    const raw = await request<{ success: boolean; data: McpServerSyncResponse }>(
      `/mcp-servers/${id}/sync`,
      { method: "POST" },
    );
    return { ...raw, data: toCamelCase(raw.data) as McpServerSyncResponse };
  },

  tools: async (id: string) => {
    const raw = await request<{ success: boolean; data: McpServerTool[] }>(
      `/mcp-servers/${id}/tools`,
    );
    return {
      ...raw,
      data: raw.data.map((t) => toCamelCase(t) as McpServerTool),
    };
  },

  update: async (id: string, data: McpBindingPatch) => {
    const body = {
      is_enabled: data.isEnabled,
      auto_sync: data.autoSync,
    };
    // Drop undefined keys so the PATCH only carries fields the caller set.
    const clean = Object.fromEntries(
      Object.entries(body).filter(([, v]) => v !== undefined),
    );
    const raw = await request<{ success: boolean; data: McpServer }>(
      `/mcp-servers/${id}`,
      { method: "PATCH", body: JSON.stringify(clean) },
    );
    return { ...raw, data: toCamelCase(raw.data) as McpServer };
  },

  delete: (id: string, cascade = false) =>
    request<{ success: boolean }>(
      `/mcp-servers/${id}${cascade ? "?cascade=true" : ""}`,
      { method: "DELETE" },
    ),

  /** 内置企业连接器目录（静态目录 + 本租户绑定状态合并）。 */
  connectors: async () => {
    const raw = await request<{ success: boolean; data: McpConnector[] }>(
      "/mcp-connectors",
    );
    return {
      ...raw,
      data: raw.data.map((c) => toCamelCase(c) as McpConnector),
    };
  },
};

// ---------------------------------------------------------------------------
// 外部数据源（对话输入框的「外部数据」选择面）
// ---------------------------------------------------------------------------

export const connectorsApi = {
  /**
   * 全部外部数据源的统一视图：目录 + 本租户接入状态 + **当前登录用户本人**的
   * 授权状态。与 `mcpServersApi.connectors()`（管理页的可注册目录）不是同一个
   * 端点，两者不可互相替代。
   */
  list: async () => {
    const raw = await request<{ success: boolean; data: Connector[] }>(
      "/connectors",
    );
    return {
      ...raw,
      data: raw.data.map((c) => toCamelCase(c) as Connector),
    };
  },
};

// ---------------------------------------------------------------------------
// Prompt Templates (quick-prompt picker for the chat composer, W7)
// ---------------------------------------------------------------------------

export const promptTemplatesApi = {
  list: async () => {
    const raw = await request<{ success: boolean; data: PromptTemplate[] }>("/prompt-templates");
    return { ...raw, data: raw.data.map((item) => toCamelCase(item) as PromptTemplate) };
  },
};

// ---------------------------------------------------------------------------
// Prompt Optimization (rewrite a rough composer input into a full prompt, W8)
// ---------------------------------------------------------------------------

export const promptOptimizationApi = {
  optimize: async (content: string, context?: string) => {
    const raw = await request<{ success: boolean; data: { optimizedContent: string } }>(
      "/ai/prompt-optimization",
      { method: "POST", body: JSON.stringify({ content, context }) },
    );
    return raw.data.optimizedContent;
  },
};

// ---------------------------------------------------------------------------
// Knowledge (Business Knowledge)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// 合同要素批量提取任务（1B-3）
// ---------------------------------------------------------------------------

export const extractionJobsApi = {
  /** 选一批文档批量提取要素（202 返回 pending 任务，后台提交引擎）。 */
  create: (data: ExtractionJobCreateRequest) =>
    request<Envelope<ExtractionJob>>(`/extraction-jobs`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  list: (params?: { matterId?: string; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.matterId) qs.set("matterId", params.matterId);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<ExtractionJob>>(`/extraction-jobs?${qs}`);
  },

  get: (jobId: string) => request<Envelope<ExtractionJob>>(`/extraction-jobs/${jobId}`),
};

export const knowledgeApi = {
  list: async (params?: {
    type?: string;
    categoryId?: string;
    scope?: string;
    search?: string;
    isBinding?: boolean;
    page?: number;
    pageSize?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.type) qs.set("type", params.type);
    if (params?.categoryId) qs.set("category_id", params.categoryId);
    if (params?.scope) qs.set("scope", params.scope);
    if (params?.search) qs.set("search", params.search);
    // Slice by the 模板与标准 binding flag server-side so each resource-center tab
    // pulls only its own set (avoids binding templates being pushed past the page
    // boundary by learning materials once a tenant exceeds one page).
    if (params?.isBinding !== undefined) qs.set("is_binding", String(params.isBinding));
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    const raw = await request<PaginatedResponse<KnowledgeItem>>(`/knowledge?${qs}`);
    return transformPaginated(raw);
  },

  get: async (id: string) => {
    const raw = await request<{ success: boolean; data: KnowledgeItem }>(`/knowledge/${id}`);
    return { ...raw, data: toCamelCase(raw.data) };
  },

  /**
   * 1A-4：以范本正文为起点开一份起草工作台草稿（范本原文落入编辑器）。
   *
   * 响应附带 ``sourceHasTables``（2026-08-26 表格插入批次，评估文档轴四）：
   * 文件型范本源文档含表格时为 true，调用方据此弹一次性告警；纯文本范本
   * 恒为 false。
   */
  openAsDraftDoc: (id: string, data?: { title?: string; matterId?: string }) =>
    request<DraftDocOpenResponse>(`/knowledge/${id}/open-as-draft-doc`, {
      method: "POST",
      body: JSON.stringify(data ?? {}),
    }),

  create: async (data: KnowledgeItemCreateRequest) => {
    const raw = await request<{ success: boolean; data: KnowledgeItem }>("/knowledge", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return { ...raw, data: toCamelCase(raw.data) };
  },

  upload: async (formData: FormData) => {
    const raw = await requestFormData<{ success: boolean; data: KnowledgeItem }>("/knowledge/upload", {
      method: "POST",
      body: formData,
    });
    return { ...raw, data: toCamelCase(raw.data) };
  },

  update: async (id: string, data: KnowledgeItemUpdateRequest) => {
    const raw = await request<{ success: boolean; data: KnowledgeItem }>(`/knowledge/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    return { ...raw, data: toCamelCase(raw.data) };
  },

  delete: (id: string) =>
    request<{ success: boolean }>(`/knowledge/${id}`, { method: "DELETE" }),

  toggleActive: (id: string) =>
    request<{ success: boolean; data: { id: string; isActive: boolean } }>(
      `/knowledge/${id}/toggle-active`,
      { method: "POST" },
    ),

  download: (id: string) =>
    requestFormDataBlob(`/knowledge/${id}/download`),

  replaceFile: async (id: string, formData: FormData) => {
    const raw = await requestFormData<{ success: boolean; data: KnowledgeItem }>(`/knowledge/${id}/replace-file`, {
      method: "POST",
      body: formData,
    });
    return { ...raw, data: toCamelCase(raw.data) };
  },
};

// 外部资源导航（ADR 2026-07-09 决策五）：只读精选站点链接，AI 不读取。
export const externalLinksApi = {
  list: async () => {
    const raw = await request<{ success: boolean; data: ExternalLink[] }>("/external-links");
    return { ...raw, data: raw.data.map((item) => toCamelCase(item) as ExternalLink) };
  },
};

// ---------------------------------------------------------------------------
// Knowledge Categories
// ---------------------------------------------------------------------------

export const knowledgeCategoriesApi = {
  list: async () => {
    const raw = await request<{ success: boolean; data: KnowledgeCategory[] }>("/knowledge/categories");
    return { ...raw, data: raw.data.map(toCamelCase) };
  },

  create: async (data: KnowledgeCategoryCreateRequest) => {
    const raw = await request<{ success: boolean; data: KnowledgeCategory }>("/knowledge/categories", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return { ...raw, data: toCamelCase(raw.data) };
  },

  update: async (id: string, data: KnowledgeCategoryUpdateRequest) => {
    const raw = await request<{ success: boolean; data: KnowledgeCategory }>(`/knowledge/categories/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    return { ...raw, data: toCamelCase(raw.data) };
  },

  move: async (id: string, data: KnowledgeCategoryMoveRequest) => {
    const raw = await request<{ success: boolean; data: KnowledgeCategory }>(`/knowledge/categories/${id}/move`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    return { ...raw, data: toCamelCase(raw.data) };
  },

  delete: (id: string) =>
    request<{ success: boolean }>(`/knowledge/categories/${id}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Pipeline Executions
// ---------------------------------------------------------------------------

export const pipelineExecutionsApi = {
  list: (params?: {
    status?: string;
    triggerSource?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.triggerSource) qs.set("trigger_source", params.triggerSource);
    if (params?.search) qs.set("search", params.search);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<PipelineExecution>>(`/pipeline-executions?${qs}`);
  },

  get: (id: string) =>
    request<{ success: boolean; data: PipelineExecution }>(`/pipeline-executions/${id}`),

  cancel: (id: string) =>
    request<{ success: boolean; data: PipelineExecution }>(
      `/pipeline-executions/${id}/cancel`,
      { method: "POST" },
    ),

  retry: (id: string) =>
    request<{ success: boolean; data: PipelineExecution }>(
      `/pipeline-executions/${id}/retry`,
      { method: "POST" },
    ),
};

// ---------------------------------------------------------------------------
// Pipeline Templates (for management in resource center)
// ---------------------------------------------------------------------------

export const pipelineTemplatesApi = {
  list: (params?: {
    scope?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.scope) qs.set("scope", params.scope);
    if (params?.search) qs.set("search", params.search);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<PipelineTemplate>>(`/pipeline-templates?${qs}`);
  },

  get: (id: string) =>
    request<{ success: boolean; data: PipelineTemplate }>(`/pipeline-templates/${id}`),

  getDetail: (id: string) =>
    request<{ success: boolean; data: PipelineTemplateDetail }>(
      `/pipeline-templates/${id}/detail`,
    ),

  create: (data: { title: string; description?: string; content?: string; scope?: string; tags?: string[] }) =>
    request<{ success: boolean; data: PipelineTemplate }>("/pipeline-templates", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: { title?: string; description?: string; content?: string; tags?: string[]; isActive?: boolean }) =>
    request<{ success: boolean; data: PipelineTemplate }>(`/pipeline-templates/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<{ success: boolean }>(`/pipeline-templates/${id}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Pipeline Instances (three-tier middle layer — iteration 6 stage 2 I1)
// ---------------------------------------------------------------------------

/** Payload for `POST /pipeline-instances`. `inputs` may be partial. */
export interface PipelineInstanceCreatePayload {
  templateId: string;
  name: string;
  description?: string;
  matterId?: string | null;
  inputs?: Record<string, unknown>;
}

/** Payload for `PATCH /pipeline-instances/{id}`. Draft only. */
export interface PipelineInstanceUpdatePayload {
  name?: string;
  description?: string | null;
  matterId?: string | null;
  inputs?: Record<string, unknown>;
}

/** Executor config overrides sent on POST /ready (D3 §2). Mode is NOT exposed. */
export interface ExecutorConfigPayload {
  timeoutSeconds: number;
  stepTimeoutSeconds: number;
  onFailure: "pause" | "abort";
}

/** Response of `POST /pipeline-instances/{id}/run`. */
export interface PipelineInstanceRunResponse {
  success: boolean;
  data: {
    /** Snake-cased literal — the run endpoint returns a raw dict (no aliasing). */
    execution_id: string;
    instance: PipelineInstanceDetail;
  };
}

export const pipelineInstancesApi = {
  /** Full instance detail. Backend serialises camelCase (JerryResponse). */
  get: (id: string) =>
    request<{ success: boolean; data: PipelineInstanceDetail }>(
      `/pipeline-instances/${id}`,
    ),

  /**
   * List instances for the current tenant.
   *
   * `scope` defaults to `mine`; `all` requires tenant_admin. Archived instances
   * are always excluded by the backend. `status` is a single enum value.
   */
  list: (params?: {
    status?: string;
    templateId?: string;
    matterId?: string;
    search?: string;
    scope?: "mine" | "all";
    page?: number;
    pageSize?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.templateId) qs.set("template_id", params.templateId);
    if (params?.matterId) qs.set("matter_id", params.matterId);
    if (params?.search) qs.set("search", params.search);
    qs.set("scope", params?.scope ?? "mine");
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<{
      success: boolean;
      data: PipelineInstanceListItem[];
      meta: { page: number; pageSize: number; total: number; totalPages: number };
    }>(`/pipeline-instances?${qs}`);
  },

  /** Create a draft instance bound to a template. */
  create: (payload: PipelineInstanceCreatePayload) =>
    request<{ success: boolean; data: PipelineInstanceDetail }>(
      "/pipeline-instances",
      {
        method: "POST",
        body: JSON.stringify({
          template_id: payload.templateId,
          name: payload.name,
          description: payload.description,
          matter_id: payload.matterId ?? null,
          inputs: payload.inputs ?? {},
        }),
      },
    ),

  /** Edit inputs/name/matter (draft only). */
  update: (id: string, payload: PipelineInstanceUpdatePayload) =>
    request<{ success: boolean; data: PipelineInstanceDetail }>(
      `/pipeline-instances/${id}`,
      {
        method: "PATCH",
        body: JSON.stringify({
          name: payload.name,
          description: payload.description,
          matter_id: payload.matterId,
          inputs: payload.inputs,
        }),
      },
    ),

  /** Soft-delete (archive) a draft instance. */
  delete: (id: string) =>
    request<{ success: boolean }>(`/pipeline-instances/${id}`, {
      method: "DELETE",
    }),

  /** Transition draft → queued; validates inputs against the template. */
  ready: (id: string, payload?: { executorConfig?: ExecutorConfigPayload }) =>
    request<{ success: boolean; data: PipelineInstanceDetail }>(
      `/pipeline-instances/${id}/ready`,
      {
        method: "POST",
        body: JSON.stringify(
          payload?.executorConfig
            ? { executor_config: payload.executorConfig }
            : {},
        ),
      },
    ),

  /** Dispatch a new execution for a queued/completed instance. */
  run: (id: string) =>
    request<PipelineInstanceRunResponse>(`/pipeline-instances/${id}/run`, {
      method: "POST",
      body: JSON.stringify({}),
    }),

  /** Pause a running instance. */
  pause: (id: string) =>
    request<{ success: boolean; data: PipelineInstanceDetail }>(
      `/pipeline-instances/${id}/pause`,
      { method: "POST", body: JSON.stringify({}) },
    ),

  /** Resume a paused/failed instance; `strategy` applies to failed steps. */
  resume: (id: string, strategy: "retry" | "skip" = "retry") =>
    request<{ success: boolean; data: PipelineInstanceDetail }>(
      `/pipeline-instances/${id}/resume`,
      { method: "POST", body: JSON.stringify({ strategy }) },
    ),
};

// ---------------------------------------------------------------------------
// Scheduled Tasks (定时任务)
//
// Contract: docs/iterations/scheduled-tasks-v1-plan.md §6. Unlike most other
// jerry list endpoints, the query params for GET /scheduled-tasks are
// camelCase per the frozen contract (targetType/matterId/instanceId), not
// snake_case — do not "fix" that to match pipelineInstancesApi.
//
// 嵌套 JSONB 键适配（波次 2 契约对齐）：
// - resolverConfig 的存储/校验键为 snake_case（max_documents / first_run_window_days），
//   前端类型为 camelCase——提交时转 snake，响应时转回 camel。
// - resolvedInputs 快照键为 wire camelCase（后端有意如此），其中命名字段差异：
//   后端 totalMatched/kept ↔ 前端 totalHit/documentIds.length。
// ---------------------------------------------------------------------------

type WireResolverConfig = {
  max_documents?: number | null;
  first_run_window_days?: number | null;
} & Record<string, unknown>;

const toWireResolverConfig = (
  cfg: ScheduledTask["resolverConfig"],
): WireResolverConfig | null => {
  if (!cfg) return cfg ?? null;
  const { maxDocuments, firstRunWindowDays, ...rest } = cfg;
  return {
    ...rest,
    ...(maxDocuments != null ? { max_documents: maxDocuments } : {}),
    ...(firstRunWindowDays != null
      ? { first_run_window_days: firstRunWindowDays }
      : {}),
  };
};

const fromWireResolverConfig = (
  cfg: WireResolverConfig | null | undefined,
): ScheduledTask["resolverConfig"] => {
  if (!cfg) return cfg ?? null;
  const { max_documents, first_run_window_days, ...rest } = cfg;
  return {
    ...rest,
    ...(max_documents != null ? { maxDocuments: max_documents } : {}),
    ...(first_run_window_days != null
      ? { firstRunWindowDays: first_run_window_days }
      : {}),
  } as ScheduledTask["resolverConfig"];
};

const mapScheduledTask = (task: ScheduledTask): ScheduledTask => ({
  ...task,
  resolverConfig: fromWireResolverConfig(
    task.resolverConfig as unknown as WireResolverConfig | null,
  ),
});

type WireResolvedInputs = {
  documentIds?: string[];
  totalMatched?: number;
  kept?: number;
  truncated?: boolean;
  periodStart?: string;
  periodEnd?: string;
} & Record<string, unknown>;

const mapScheduledTaskRun = (run: ScheduledTaskRun): ScheduledTaskRun => {
  const raw = run.resolvedInputs as unknown as WireResolvedInputs | null;
  if (!raw) return run;
  return {
    ...run,
    resolvedInputs: {
      ...raw,
      documentIds: raw.documentIds ?? [],
      totalHit: raw.totalMatched ?? raw.documentIds?.length ?? 0,
      truncated: raw.truncated ?? false,
      periodStart: raw.periodStart ?? null,
      periodEnd: raw.periodEnd ?? null,
    } as ScheduledTaskRun["resolvedInputs"],
  };
};

export const scheduledTasksApi = {
  /**
   * List scheduled tasks for the current tenant.
   *
   * `scope` defaults to `mine` (created_by === current user); `all` returns
   * every task in the tenant. `q` is a free-text search over name.
   */
  list: (params?: {
    status?: ScheduledTask["status"];
    targetType?: ScheduledTask["targetType"];
    matterId?: string;
    instanceId?: string;
    q?: string;
    scope?: "mine" | "all";
    page?: number;
    pageSize?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.targetType) qs.set("targetType", params.targetType);
    if (params?.matterId) qs.set("matterId", params.matterId);
    if (params?.instanceId) qs.set("instanceId", params.instanceId);
    if (params?.q) qs.set("q", params.q);
    qs.set("scope", params?.scope ?? "mine");
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("pageSize", String(params.pageSize));
    return request<PaginatedResponse<ScheduledTask>>(`/scheduled-tasks?${qs}`).then(
      (res) => ({ ...res, data: res.data.map(mapScheduledTask) }),
    );
  },

  /** Overview counts for the summary bar. Registered before `/{id}` on the backend. */
  summary: () =>
    request<Envelope<ScheduledTaskSummary>>(`/scheduled-tasks/summary`),

  /** Full task detail (list item and detail share the same shape). */
  get: (id: string) =>
    request<Envelope<ScheduledTask>>(`/scheduled-tasks/${id}`).then((res) => ({
      ...res,
      data: mapScheduledTask(res.data),
    })),

  /** Create a task. Body fields are already camelCase (JerryRequest alias). */
  create: (payload: ScheduledTaskCreatePayload) =>
    request<Envelope<ScheduledTask>>("/scheduled-tasks", {
      method: "POST",
      body: JSON.stringify({
        ...payload,
        resolverConfig: toWireResolverConfig(payload.resolverConfig ?? null),
      }),
    }).then((res) => ({ ...res, data: mapScheduledTask(res.data) })),

  /** Edit a task. Cannot change `targetType` — create a new task instead. */
  update: (id: string, payload: ScheduledTaskUpdatePayload) =>
    request<Envelope<ScheduledTask>>(`/scheduled-tasks/${id}`, {
      method: "PATCH",
      body: JSON.stringify({
        ...payload,
        ...(payload.resolverConfig !== undefined
          ? { resolverConfig: toWireResolverConfig(payload.resolverConfig) }
          : {}),
      }),
    }).then((res) => ({ ...res, data: mapScheduledTask(res.data) })),

  /** Soft-delete a task. */
  delete: (id: string) =>
    request<Envelope<null>>(`/scheduled-tasks/${id}`, { method: "DELETE" }),

  /** active → paused; server clears `nextRunAt`. 409 if not currently active. */
  pause: (id: string) =>
    request<Envelope<ScheduledTask>>(`/scheduled-tasks/${id}/pause`, {
      method: "POST",
      body: JSON.stringify({}),
    }).then((res) => ({ ...res, data: mapScheduledTask(res.data) })),

  /** paused → active; server recomputes `nextRunAt`. 409 if not currently paused. */
  resume: (id: string) =>
    request<Envelope<ScheduledTask>>(`/scheduled-tasks/${id}/resume`, {
      method: "POST",
      body: JSON.stringify({}),
    }).then((res) => ({ ...res, data: mapScheduledTask(res.data) })),

  /**
   * Fire the task immediately (trigger_kind=manual). Does not change
   * `nextRunAt`/`runCount`. Returns the created audit row; 409 if the
   * underlying pipeline instance already has an active execution.
   */
  runNow: (id: string) =>
    request<Envelope<ScheduledTaskRun>>(`/scheduled-tasks/${id}/run-now`, {
      method: "POST",
      body: JSON.stringify({}),
    }).then((res) => ({ ...res, data: mapScheduledTaskRun(res.data) })),

  /** Execution history, paginated, newest `scheduledAt` first. */
  listRuns: (id: string, params?: { page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("pageSize", String(params.pageSize));
    return request<PaginatedResponse<ScheduledTaskRun>>(
      `/scheduled-tasks/${id}/runs?${qs}`,
    ).then((res) => ({ ...res, data: res.data.map(mapScheduledTaskRun) }));
  },

  // -- 期产物人审闭环（波次 2，pipeline_template 专属）------------------------
  //
  // 请求体键名沿用 scheduled-tasks 契约的 camelCase（JerryRequest alias）。

  /** 期产物视图：步骤产物 + 已另存文档 + 逐条表态 + 上期摘要 + 人审状态。 */
  runReview: (taskId: string, runId: string) =>
    request<Envelope<ScheduledRunReview>>(
      `/scheduled-tasks/${taskId}/runs/${runId}/review`,
    ).then((res) => ({
      ...res,
      data: { ...res.data, run: mapScheduledTaskRun(res.data.run) },
    })),

  /** 整期采纳（note 选填）。成功后以 runReview 重读留痕。 */
  acceptRun: (taskId: string, runId: string, body?: { note?: string | null }) =>
    request<Envelope<unknown>>(`/scheduled-tasks/${taskId}/runs/${runId}/accept`, {
      method: "POST",
      body: JSON.stringify({ note: body?.note ?? null }),
    }),

  /** 整期退回（reason 必填，服务端 422 校验）。 */
  returnRun: (taskId: string, runId: string, body: { reason: string }) =>
    request<Envelope<unknown>>(`/scheduled-tasks/${taskId}/runs/${runId}/return`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** 把本期产物另存为事项文档；返回直达审查/比对的动作参数。 */
  saveRunAsDocument: (
    taskId: string,
    runId: string,
    body?: { documentName?: string },
  ) =>
    request<Envelope<ScheduledRunSaveAsDocumentResult>>(
      `/scheduled-tasks/${taskId}/runs/${runId}/save-as-document`,
      { method: "POST", body: JSON.stringify(body ?? {}) },
    ),
};

// ---------------------------------------------------------------------------
// AI Verdicts — 统一表态写入端点（波次 2）
//
// POST /ai-verdicts {targetKind, targetId, targetRef, verdict(agree|disagree),
// reason?, category?}。读取侧不单列端点：期产物视图的 verdicts[] 随 review
// 载荷一并返回。
// ---------------------------------------------------------------------------

export const aiVerdictsApi = {
  create: (payload: AiVerdictCreatePayload) =>
    request<Envelope<AiVerdict>>("/ai-verdicts", {
      method: "POST",
      body: JSON.stringify({
        targetKind: payload.targetKind,
        targetId: payload.targetId,
        targetRef: payload.targetRef,
        verdict: payload.verdict,
        reason: payload.reason ?? null,
        category: payload.category ?? null,
      }),
    }),
};

// ---------------------------------------------------------------------------
// Document Grants (sharing)
// ---------------------------------------------------------------------------

export const documentGrantsApi = {
  list: (documentId: string) =>
    request<{ success: boolean; data: DocumentGrantsResponse }>(
      `/documents/${documentId}/grants`,
    ),

  create: (documentId: string, data: {
    principalType: 'user';
    principalId: string;
    accessLevel: DocAccessLevel;
    expiresAt?: string | null;
  }) =>
    request<{ success: boolean; data: DocumentGrant }>(
      `/documents/${documentId}/grants`,
      { method: 'POST', body: JSON.stringify(data) },
    ),

  update: (documentId: string, grantId: string, data: {
    accessLevel?: DocAccessLevel;
    expiresAt?: string | null;
  }) =>
    request<{ success: boolean; data: DocumentGrant }>(
      `/documents/${documentId}/grants/${grantId}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    ),

  revoke: (documentId: string, grantId: string) =>
    request<{ success: boolean }>(
      `/documents/${documentId}/grants/${grantId}`,
      { method: 'DELETE' },
    ),
};

// ---------------------------------------------------------------------------
// External Shares
// ---------------------------------------------------------------------------

export const externalSharesApi = {
  list: (documentId: string) =>
    request<{ success: boolean; data: ExternalShare[] }>(
      `/documents/${documentId}/external-shares`,
    ),

  create: (documentId: string, data: {
    accessLevel: 'view';
    expiresAt: string;
    maxViews?: number | null;
  }) =>
    request<{ success: boolean; data: ExternalShareCreated }>(
      `/documents/${documentId}/external-shares`,
      { method: 'POST', body: JSON.stringify(data) },
    ),

  revoke: (documentId: string, shareId: string) =>
    request<{ success: boolean }>(
      `/documents/${documentId}/external-shares/${shareId}`,
      { method: 'DELETE' },
    ),
};

// ---------------------------------------------------------------------------
// Public Share (unauthenticated)
// ---------------------------------------------------------------------------

/** Unauthenticated request for public share endpoints — no auth header injected. */
async function publicRequest<T>(path: string): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_PREFIX}${path}`, {
      signal: controller.signal,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const message = body?.error?.message || `API error: ${res.status}`;
      throw new ApiError(res.status, message);
    }
    return res.json() as Promise<T>;
  } catch (err: unknown) {
    if (err instanceof ApiError) throw err;
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiError(0, 'Request timed out');
    }
    throw new ApiError(0, err instanceof Error ? err.message : 'Network error');
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Unauthenticated content fetch for public share — no auth header injected.
 *
 * The active path streams binary bytes; a CAS-race loser instead gets a JSON
 * `{ success:false, data:{ state } }` body. Branch on the response Content-Type
 * so the page can render either the document (blob) or the matching state page
 * (exhausted/expired/revoked/invalid). 429 and other HTTP errors still throw
 * `ApiError` with the original status, matching `publicRequest`.
 */
async function publicRequestBlob(path: string): Promise<PublicShareContentResult> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_PREFIX}${path}`, {
      signal: controller.signal,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const message = body?.error?.message || `API error: ${res.status}`;
      throw new ApiError(res.status, message);
    }

    const contentType = res.headers.get('content-type') ?? '';

    // JSON body (200 OK) — a concurrent request won the last view; surface state.
    if (contentType.includes('application/json')) {
      const body = (await res.json().catch(() => ({}))) as {
        data?: { state?: PublicShareState };
      };
      const state = body?.data?.state;
      // Anything but 'active' is a terminal state page. Treat a missing/active
      // state on a JSON body as 'invalid' (defensive — the bytes never arrived).
      const resolved: Exclude<PublicShareState, 'active'> =
        state && state !== 'active' ? state : 'invalid';
      return { kind: 'state', state: resolved };
    }

    // Binary bytes — the happy path.
    const blob = await res.blob();
    return { kind: 'blob', blob };
  } catch (err: unknown) {
    if (err instanceof ApiError) throw err;
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiError(0, 'Request timed out');
    }
    throw new ApiError(0, err instanceof Error ? err.message : 'Network error');
  } finally {
    clearTimeout(timeoutId);
  }
}

export const publicShareApi = {
  getMeta: (token: string) =>
    publicRequest<{ success: boolean; data: PublicShareMeta }>(
      `/public/shares/${token}`,
    ),

  getContent: (token: string): Promise<PublicShareContentResult> =>
    publicRequestBlob(`/public/shares/${token}/content`),
};

// ---------------------------------------------------------------------------
// Document Ownership Transfer
// ---------------------------------------------------------------------------

export const documentOwnershipApi = {
  transfer: (documentId: string, data: {
    ownerScope: DocOwnerScope;
    ownerScopeId?: string;
  }) =>
    request<{ success: boolean; data: Document }>(
      `/documents/${documentId}/ownership`,
      { method: 'PATCH', body: JSON.stringify(data) },
    ),
};

// ---------------------------------------------------------------------------
// Principals search (users, tenant-scoped)
// ---------------------------------------------------------------------------

export const principalsApi = {
  search: (params: { q: string; type?: 'user'; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    qs.set('q', params.q);
    if (params.type) qs.set('type', params.type);
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('page_size', String(params.pageSize));
    return request<{ success: boolean; data: PrincipalOption[]; meta: { total: number } }>(
      `/principals/search?${qs}`,
    );
  },
};

// ---------------------------------------------------------------------------
// VeriDoc — document comparison (/api/v1/jerry/vd/*)
//
// The veridoc component is merged into the jerry backend under the same
// `/api/v1/jerry` prefix, mounted at `/vd`. Unlike the jerry CRUD endpoints,
// the veridoc routes return bare objects / arrays (no `{ success, data }`
// envelope) and already emit camelCase (`response_model_by_alias=True`), so
// no key transform is applied here.
// ---------------------------------------------------------------------------

const VERIDOC_PATH = "/vd";

/**
 * AI document review (jerry-owned, ADR §2.5) — independent of Veri Doc compare.
 * `trigger` runs a doc-review on the initiating lawyer's behalf; `listFindings`
 * reads the latest result. Both return the run-level review conclusion plus its
 * per-clause findings, unwrapping the `{success, data}` envelope to `data`.
 */
export const aiReviewApi = {
  /** Trigger one AI review; returns the run-level review + findings produced. */
  trigger: (documentId: string, body?: { stance?: string }) =>
    request<{ success: boolean; data: AiReviewResult }>(
      `/documents/${documentId}/ai-review`,
      {
        method: "POST",
        body: JSON.stringify(body ?? {}),
        // The endpoint blocks until the doc-review skill finishes (submit +
        // poll the engine task), which runs an LLM over the whole document.
        // Observed ~24 s for a ready contract; the backend's own poll ceiling
        // is 180 s, so the 10 s default aborts a request the server completes
        // successfully. Allow the same generous budget as other long calls.
        timeoutMs: 300_000,
      },
    ),

  /** Read the latest AI-review result (review conclusion + findings). */
  listFindings: (documentId: string) =>
    request<{ success: boolean; data: AiReviewResult }>(
      `/documents/${documentId}/ai-findings`,
    ),

  /**
   * 对一条发现表态：采纳 / 不认同 / 撤销表态。持久化在服务端，刷新与重跑后
   * 仍在；重跑时判为不认同（误报）的发现沿用该表态，不再重复打扰律师。
   *
   * `reasonCode` / `reasonText` 都是选填，且仅在 processed="rejected" 时可传
   * （其余取值携带理由服务端返回 422）——否决一次点击即完成。
   */
  updateFinding: (
    documentId: string,
    findingId: string,
    body: {
      processed: AiFindingProcessed;
      reasonCode?: AiVerdictReasonCode | null;
      reasonText?: string | null;
    },
  ) =>
    request<{ success: boolean; data: AiFinding }>(
      `/documents/${documentId}/ai-findings/${findingId}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),

  /**
   * AF2：把最近一次审查的结论与全部逐条风险另存为一份事项文档（docx）。
   *
   * 与「导出审查意见」并列的第二条出路：导出交给外部，另存留在事项内供团队接力。
   * 正文由服务端汇总，请求不带正文；``documentName`` 省略时服务端按
   * 「<文档标题>-审查意见」命名。被审查文档未挂事项时服务端 409，需补 matterId。
   */
  saveReportAsDocument: (
    documentId: string,
    body?: { documentName?: string; matterId?: string },
  ) =>
    request<{ success: boolean; data: Document }>(
      `/documents/${documentId}/ai-review/save-as-document`,
      {
        method: "POST",
        body: JSON.stringify(body ?? {}),
      },
    ),

  /**
   * 决策 2026-08-08 §4：把文档引擎提取的全文另存为可编辑工作台草稿。
   *
   * 审查工作台「插入修改」的载体：不编辑原件，把提取文本灌进新草稿，
   * 返回的草稿带 ``sourceDocumentId`` 溯源，前端随即跳转起草工作台并在右栏
   * 接入该文档的审查发现。引擎侧不可用（无索引映射 / 索引失败 / 引擎 404 /
   * 空文本）时服务端 409 可读文案。
   *
   * 响应附带 ``sourceHasTables``（2026-08-26 表格插入批次，评估文档轴四）：
   * 源文档含表格时为 true，调用方据此弹一次性告警。
   */
  saveAsDraftDoc: (
    documentId: string,
    body?: DocumentSaveAsDraftDocRequest,
  ) =>
    request<DraftDocOpenResponse>(
      `/documents/${documentId}/save-as-draft-doc`,
      {
        method: "POST",
        body: JSON.stringify(body ?? {}),
      },
    ),
};

// ---------------------------------------------------------------------------
// AI Review Batches — 批量 AI 审查（波次 2 契约）
//
//   POST /documents/ai-review-batches {documentIds[], stance?} → 202 {id}
//   GET  /ai-review-batches/{id}                 → 批详情（含 items 全量行）
//   GET  /ai-review-batches?matterId=            → 分页批列表
//   POST /ai-review-batches/{id}/items/{itemId}/retry → 202 新 item id
//
// 批在后台逐份执行，详情由前端轮询（见 queries.ts useAiReviewBatch）。
// ---------------------------------------------------------------------------

export const aiReviewBatchesApi = {
  /** 发起一批批量审查。202 异步受理，只回新批 id。 */
  create: (data: { documentIds: string[]; stance?: string | null }) =>
    request<Envelope<{ id: string }>>("/documents/ai-review-batches", {
      method: "POST",
      body: JSON.stringify({
        documentIds: data.documentIds,
        stance: data.stance ?? null,
      }),
    }),

  /** 批详情（进度条与行级表格的数据源）。 */
  get: (id: string) => request<Envelope<AiReviewBatch>>(`/ai-review-batches/${id}`),

  /** 批列表（挂文档库的「批量审查记录」入口）。 */
  list: (params?: { matterId?: string; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.matterId) qs.set("matterId", params.matterId);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("pageSize", String(params.pageSize));
    return request<PaginatedResponse<AiReviewBatch>>(`/ai-review-batches?${qs}`);
  },

  /** 重试一个失败行；202 受理，返回新 item id（原行保留为审计痕迹）。 */
  retryItem: (batchId: string, itemId: string) =>
    request<Envelope<{ id: string }>>(
      `/ai-review-batches/${batchId}/items/${itemId}/retry`,
      { method: "POST", body: JSON.stringify({}) },
    ),
};

export const veridocApi = {
  // Note: veri_doc R4 (ADR-118) moved document listing out of the engine core;
  // GET /documents and /documents/{id}/versions no longer exist. The matter
  // compare slice uploads a pair and compares directly, so they are not needed.
  /**
   * Upload exactly two PDF/DOCX files, paired into one temporary veridoc
   * document with two versions ready to compare.
   */
  upload: async (data: { files: [File, File]; name?: string }) => {
    const formData = new FormData();
    formData.append("files", data.files[0]);
    formData.append("files", data.files[1]);
    if (data.name) formData.append("name", data.name);
    return requestFormData<VeridocDocumentSummary>(`${VERIDOC_PATH}/upload`, {
      method: "POST",
      body: formData,
    });
  },

  /** Create a comparison between two versions; returns the full changes list. */
  createComparison: (data: CreateComparisonRequest) =>
    request<ComparisonResponse>(`${VERIDOC_PATH}/comparisons`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  /** Fetch a comparison record and its changes by id. */
  getComparison: (id: string) =>
    request<ComparisonResponse>(`${VERIDOC_PATH}/comparisons/${id}`),

  // ─── c1/c2/c3 jerry wrapper methods ──────────────────────────────────────
  //
  // These hit jerry's wrapper endpoints (not veridoc's own /vd/* paths).
  // Paths are relative to API_PREFIX (`/api/v1/jerry`), so the resolved URLs
  // become `/api/v1/jerry/comparisons/...` and `/api/v1/jerry/documents/...`.
  // Every wrapper response is wrapped in `{success: boolean, data: T}`; these
  // methods unwrap and return `data` directly (DELETE returns the envelope
  // `{success: true}` as-is, per jerry convention). Errors are surfaced via
  // `ApiError` from the shared `request<T>` helper (status + code + message).
  //
  // Divergence note: unlike every other API object in this file, which return
  // the full envelope and force callers to access `.data`, these methods
  // unwrap at the API layer so TanStack Query hooks can consume the result
  // directly. Do NOT write `veridocApi.listComparisons(...).data` — that
  // yields `undefined`.

  /** c1: accept / reject / pending a comparison change. */
  async setDecision(
    jerryCmpId: string,
    changeId: string,
    status: DecisionStatus,
  ): Promise<ReviewDecisionResponse> {
    const envelope = await request<Envelope<ReviewDecisionResponse>>(
      `/comparisons/${jerryCmpId}/changes/${changeId}/decision`,
      {
        method: "PUT",
        body: JSON.stringify({ status }),
      },
    );
    return envelope.data;
  },

  /** c2: create an annotation on a change. */
  async createAnnotation(
    jerryCmpId: string,
    changeId: string,
    body: string,
  ): Promise<AnnotationResponse> {
    const envelope = await request<Envelope<AnnotationResponse>>(
      `/comparisons/${jerryCmpId}/changes/${changeId}/annotations`,
      {
        method: "POST",
        body: JSON.stringify({ body }),
      },
    );
    return envelope.data;
  },

  /** c2: edit an annotation (author only, within 5-min window). */
  async updateAnnotation(
    jerryCmpId: string,
    annotationId: string,
    body: string,
  ): Promise<AnnotationResponse> {
    const envelope = await request<Envelope<AnnotationResponse>>(
      `/comparisons/${jerryCmpId}/annotations/${annotationId}`,
      {
        method: "PATCH",
        body: JSON.stringify({ body }),
      },
    );
    return envelope.data;
  },

  /** c2: hard-delete an annotation (author only, no time limit). */
  async deleteAnnotation(
    jerryCmpId: string,
    annotationId: string,
  ): Promise<{ success: boolean }> {
    return request<{ success: boolean }>(
      `/comparisons/${jerryCmpId}/annotations/${annotationId}`,
      { method: "DELETE" },
    );
  },

  /** c2: list annotations on a change. */
  async listAnnotations(
    jerryCmpId: string,
    changeId: string,
  ): Promise<AnnotationListResponse> {
    const envelope = await request<Envelope<AnnotationListResponse>>(
      `/comparisons/${jerryCmpId}/changes/${changeId}/annotations`,
    );
    return envelope.data;
  },

  /** c3: list comparisons where the doc is source or target. */
  async listComparisons(
    documentId: string,
  ): Promise<DocumentComparisonsResponse> {
    const envelope = await request<Envelope<DocumentComparisonsResponse>>(
      `/documents/${documentId}/comparisons`,
    );
    return envelope.data;
  },

  /** b3: fetch full comparison detail (changes list). */
  async getComparisonDetail(
    jerryCmpId: string,
  ): Promise<ComparisonResponse> {
    const envelope = await request<Envelope<ComparisonResponse>>(
      `/comparisons/${jerryCmpId}`,
    );
    return envelope.data;
  },

  /**
   * b4: reverse-lookup a jerry comparison by veridoc comparison id.
   * Used by the share-link redirect entry (/compare/<vdCmpId>) to resolve
   * targetDocumentId before redirecting to /workbench/<targetId>?mode=compare.
   */
  async getComparisonByVeridocId(
    vdCmpId: string,
  ): Promise<{
    comparisonId: string;
    veridocComparisonId: string;
    sourceDocumentId: string;
    targetDocumentId: string;
  }> {
    const envelope = await request<
      Envelope<{
        comparisonId: string;
        veridocComparisonId: string;
        sourceDocumentId: string;
        targetDocumentId: string;
      }>
    >(`/comparisons/by-veridoc/${vdCmpId}`);
    return envelope.data;
  },
};

// ---------------------------------------------------------------------------
// Conflict-of-interest checks (利益冲突核查)
// ---------------------------------------------------------------------------

export const conflictChecksApi = {
  list: (params?: {
    matterId?: string;
    clientId?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.matterId) qs.set("matter_id", params.matterId);
    if (params?.clientId) qs.set("client_id", params.clientId);
    if (params?.status) qs.set("status", params.status);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<ConflictCheck>>(`/conflict-checks?${qs}`);
  },

  get: (id: string) =>
    request<Envelope<ConflictCheck>>(`/conflict-checks/${id}`),

  create: (data: ConflictCheckCreateRequest) =>
    request<Envelope<ConflictCheck>>("/conflict-checks", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: ConflictCheckUpdateRequest) =>
    request<Envelope<ConflictCheck>>(`/conflict-checks/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  changeStatus: (id: string, data: ConflictCheckStatusChangeRequest) =>
    request<Envelope<ConflictCheck>>(`/conflict-checks/${id}/status`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  remove: (id: string) =>
    request<Envelope<null>>(`/conflict-checks/${id}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// AI drafting (US-D「AI 真实起草」) — jerry orchestrates + stores results,
// the Lobster doc-draft skill only executes.
// ---------------------------------------------------------------------------

export const draftsApi = {
  /** Create (and asynchronously run) a draft; returns the "pending" record immediately. */
  create: (matterId: string, data: DraftCreateRequest) =>
    request<Envelope<Draft>>(`/matters/${matterId}/drafts`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  /** List a matter's drafting records, most recent first (BR-9: all matter members see all). */
  list: (matterId: string, params?: { page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<Draft>>(`/matters/${matterId}/drafts?${qs}`);
  },

  /** Fetch a single drafting record — used both for the initial load and generation polling. */
  get: (draftId: string) => request<Envelope<Draft>>(`/drafts/${draftId}`),

  /** Cancel a still-generating draft (409 if it already reached a terminal state). */
  cancel: (draftId: string) =>
    request<Envelope<Draft>>(`/drafts/${draftId}/cancel`, { method: "POST" }),

  /** Save the (possibly hand-edited) body as a matter document (409 unless status is "succeeded"). */
  saveAsDocument: (draftId: string, data: DraftSaveAsDocumentRequest) =>
    request<Envelope<Document>>(`/drafts/${draftId}/save-as-document`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
};

// ---------------------------------------------------------------------------
// Workbench drafts (`jerry_draft_docs`, path prefix /draft-docs) — the
// conversational drafting workbench's round-trippable draft entity, distinct
// from the one-shot drafting records above (/drafts). Design:
// docs/iterations/draft-workbench-design.md §6.1.
// ---------------------------------------------------------------------------

export const draftDocsApi = {
  /** 草稿箱列表——当前用户可见的全部工作台草稿（本人创建 ∪ 所属事项），更新时间倒序。 */
  list: (params?: DraftDocListParams) => {
    const qs = new URLSearchParams();
    if (params?.matterId) qs.set("matterId", params.matterId);
    if (params?.unassigned) qs.set("unassigned", "true");
    if (params?.docType) qs.set("docType", params.docType);
    if (params?.status) qs.set("status", params.status);
    if (params?.q) qs.set("q", params.q);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<DraftDoc>>(`/draft-docs?${qs}`);
  },

  /** 新建草稿（matterId 可选：带则事项内起草，不带即自由起草），随即跳转工作台。 */
  create: (data: DraftDocCreateRequest) =>
    request<Envelope<DraftDoc>>(`/draft-docs`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  get: (draftDocId: string) => request<Envelope<DraftDoc>>(`/draft-docs/${draftDocId}`),

  /** 更新元数据（标题/文书类型/状态/事项绑定）；matterId 按「字段是否出现」区分绑定/解绑/不改。 */
  update: (draftDocId: string, data: DraftDocUpdateRequest) =>
    request<Envelope<DraftDoc>>(`/draft-docs/${draftDocId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  /** 人工编辑保存草稿正文（不产生版本行；版本行为 AI 轮次快照）。响应顶层
   * anchorsAccepted 字段（与 success/data 同级）体现本次锚点提交是否被
   * 服务端接受，见 DraftDocBodyUpdateResponse。 */
  updateBody: (draftDocId: string, data: DraftDocBodyUpdateRequest) =>
    request<DraftDocBodyUpdateResponse>(`/draft-docs/${draftDocId}/body`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  /** 软删除（仅创建者）；已另存产生的事项文档不受影响。 */
  remove: (draftDocId: string) =>
    request<Envelope<null>>(`/draft-docs/${draftDocId}`, { method: "DELETE" }),

  /** 一键成稿：以 doc-draft 生成整篇初稿写入本草稿（202 返回一次性起草记录，
   * 进度沿用 GET /drafts/{id} 轮询）。前置：已绑定事项 + 已选文书类型 + 空稿。 */
  oneShot: (draftDocId: string, data: { instructions?: string | null }) =>
    request<Envelope<Draft>>(`/draft-docs/${draftDocId}/one-shot`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  /** 另存为事项文档（阶段 6，复用 US-D02 落库链路；正文以服务端 currentBody
   * 为准）。无事项草稿必须带 matterId（先绑定再另存）。 */
  saveAsDocument: (draftDocId: string, data: DraftDocSaveAsDocumentRequest) =>
    request<Envelope<Document>>(`/draft-docs/${draftDocId}/save-as-document`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  /** AI 轮次快照列表，按轮次升序（阶段 2 起有数据）。 */
  versions: (draftDocId: string, params?: { page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<DraftDocVersion>>(`/draft-docs/${draftDocId}/versions?${qs}`);
  },

  /**
   * D4：对草稿当前正文提交一次异步 AI 自审查（写完即审），202 返回
   * review_running 标记。镜像 `aiReviewApi.trigger` 的文档侧形状，但草稿侧
   * 端点本身即时返回（不在请求内阻塞轮询引擎），无需放宽超时。仅创建者可
   * 发起（403）；空正文 409；并发去重返回同一 run（J1 同款语义）。
   */
  triggerAiReview: (draftDocId: string, body?: { stance?: string }) =>
    request<{ success: boolean; data: AiReviewResult }>(
      `/draft-docs/${draftDocId}/ai-review/trigger`,
      {
        method: "POST",
        body: JSON.stringify(body ?? {}),
      },
    ),

  /** D4：读取草稿自审查的最新结果（运行级结论 + 逐条发现）。 */
  listAiFindings: (draftDocId: string) =>
    request<{ success: boolean; data: AiReviewResult }>(
      `/draft-docs/${draftDocId}/ai-findings`,
    ),

  /** D4：对一条草稿自审查发现表态，语义与 `aiReviewApi.updateFinding` 一致
   * （三值 + 选填否决分类与理由）。 */
  updateAiFinding: (
    draftDocId: string,
    findingId: string,
    body: {
      processed: AiFindingProcessed;
      reasonCode?: AiVerdictReasonCode | null;
      reasonText?: string | null;
    },
  ) =>
    request<{ success: boolean; data: AiFinding }>(
      `/draft-docs/${draftDocId}/ai-findings/${findingId}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),

  /**
   * D1 阶段三（提案层，ADR 2026-08-16 方案 C）：提案批次列表，按轮次降序
   * （最新批次在前），批内提案按 seqNo 升序，含决策历史（拍板 4 长期留档，
   * 供审计）。可见性与草稿本体一致。
   */
  listProposals: (draftDocId: string, params?: { page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<DraftProposalBatch>>(
      `/draft-docs/${draftDocId}/proposals?${qs}`,
    );
  },

  /** 单条提案决策（采纳/拒绝，仅创建者）。仅落决策记录——正文变化由调用方
   * 先经 `updateBody` 落库，成功后再调用本端点（严格顺序，ADR §六）。已决
   * 提案重复提交返回 409（`DRAFT_PROPOSAL_ALREADY_DECIDED`）。 */
  decideProposal: (draftDocId: string, proposalId: string, data: DraftProposalDecisionRequest) =>
    request<Envelope<DraftProposal>>(`/draft-docs/${draftDocId}/proposals/${proposalId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  /** 整批采纳（前置条件不满足返回 409 `DRAFT_PROPOSAL_BATCH_NOT_ACCEPTABLE`）
   * 或整批拒绝（无前置条件）。采纳时响应内的 `draftDoc.currentBody` 已由
   * 服务端直接写为批次结果正文（不逐条重放），调用方据此回填编辑器。 */
  decideProposalBatch: (
    draftDocId: string,
    batchId: string,
    data: DraftProposalBatchDecisionRequest,
  ) =>
    request<Envelope<{ draftDoc: DraftDoc; batch: DraftProposalBatch }>>(
      `/draft-docs/${draftDocId}/proposal-batches/${batchId}/decide-all`,
      { method: "POST", body: JSON.stringify(data) },
    ),
};

// ---------------------------------------------------------------------------
// Current-user permissions (jerry.* namespace) — drives role-aware UI gating
// ---------------------------------------------------------------------------

export const permissionsApi = {
  me: () => request<Envelope<MePermissions>>("/me/permissions"),
};

// ---------------------------------------------------------------------------
// AI Memory (US-G05，契约先行——joint-common 代理层尚未实现，见 types/memory.ts)
// ---------------------------------------------------------------------------

export const memoryApi = {
  list: () => request<{ success: boolean; data: { items: MemoryItem[] } }>("/memory"),

  create: (data: MemoryCreateRequest) =>
    request<{ success: boolean; data: MemoryItem }>("/memory", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  /** `source` 由后端置为 manual（S5-03，即便原条目是 distillation 提炼所得）。 */
  update: (id: string, data: MemoryUpdateRequest) =>
    request<{ success: boolean; data: MemoryItem }>(`/memory/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  /** 204 无响应体——`request()` 对 204 短路返回 undefined，不尝试解析空 JSON。 */
  delete: (id: string) => request<void>(`/memory/${id}`, { method: "DELETE" }),

  getConfig: () => request<{ success: boolean; data: MemoryConfig }>("/memory/config"),
};

// ---------------------------------------------------------------------------
// Meetings (会议助手)
// ---------------------------------------------------------------------------

export const meetingsApi = {
  list: (params?: MeetingListParams) => {
    const qs = new URLSearchParams();
    if (params?.source) qs.set("source", params.source);
    if (params?.sources && params.sources.length > 0) {
      // Backend reads repeated ?sources=a&sources=b as a list.
      for (const s of params.sources) qs.append("sources", s);
    }
    if (params?.status) qs.set("status", params.status);
    if (params?.processingStage) qs.set("processing_stage", params.processingStage);
    if (params?.matterId) qs.set("matter_id", params.matterId);
    if (params?.dateFrom) qs.set("date_from", params.dateFrom);
    if (params?.dateTo) qs.set("date_to", params.dateTo);
    if (params?.hasAudio !== undefined) qs.set("has_audio", String(params.hasAudio));
    if (params?.isKeyMeeting !== undefined) qs.set("is_key_meeting", String(params.isKeyMeeting));
    if (params?.meetingType) qs.set("meeting_type", params.meetingType);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<Meeting>>(`/meetings?${qs}`);
  },

  get: (id: string) => request<Envelope<Meeting>>(`/meetings/${id}`),

  create: (data: MeetingCreateRequest) =>
    request<Envelope<Meeting>>("/meetings", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: MeetingUpdateRequest) =>
    request<Envelope<Meeting>>(`/meetings/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<Envelope<{ id: string }>>(`/meetings/${id}`, { method: "DELETE" }),

  importTranscript: (id: string, data: MeetingTranscriptImportRequest) =>
    request<Envelope<Meeting>>(`/meetings/${id}/transcript`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  /**
   * Upload audio for a meeting (recorder-pen capture). Multipart/form-data.
   * Optional `durationSeconds` and `recordedAt` are captured client-side
   * (MediaRecorder doesn't yield duration directly). Replaces any prior
   * attachment on the same meeting.
   */
  uploadAudio: (
    id: string,
    file: File,
    options?: { durationSeconds?: number; recordedAt?: string },
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    if (options?.durationSeconds !== undefined) {
      formData.append("duration_seconds", String(options.durationSeconds));
    }
    if (options?.recordedAt) formData.append("recorded_at", options.recordedAt);
    return request<Envelope<Meeting>>(`/meetings/${id}/audio`, {
      method: "POST",
      body: formData,
      skipContentType: true,
      // 1 hour of opus-encoded audio is ~50 MB; allow generous transfer time.
      timeoutMs: 300_000,
    });
  },

  /**
   * Fetch a presigned URL for the meeting's audio. The URL is consumed
   * directly by an `<audio>` element. Returns 404 if the meeting has no audio.
   */
  getAudioUrl: (id: string, expiresSeconds?: number) => {
    const qs = new URLSearchParams();
    if (expiresSeconds) qs.set("expires", String(expiresSeconds));
    return request<
      Envelope<{
        url: string;
        filename: string | null;
        mime: string | null;
        sizeBytes: number | null;
        durationSeconds: number | null;
        expiresIn: number;
      }>
    >(`/meetings/${id}/audio${qs.toString() ? `?${qs}` : ""}`);
  },

  /** Remove the audio attachment from a meeting (storage + fields). */
  deleteAudio: (id: string) =>
    request<Envelope<Meeting>>(`/meetings/${id}/audio`, { method: "DELETE" }),

  /**
   * Trigger ASR transcription on a meeting's audio attachment. Returns the
   * Celery task id; caller polls `getTranscript` every 3-5s until the
   * status flips to `ready` or `failed`. 409 if transcription is already
   * running.
   */
  transcribe: (id: string, hotwords?: string[]) =>
    request<Envelope<{ taskId: string; status: string }>>(
      `/meetings/${id}/transcribe`,
      {
        method: "POST",
        body: JSON.stringify(hotwords && hotwords.length > 0 ? { hotwords } : {}),
      },
    ),

  /**
   * Lightweight transcript-state poll. Returns only ASR fields (status,
   * text-so-far, progress, error). Use this in a `useQuery` with
   * `refetchInterval` predicate while status === "pending".
   */
  getTranscript: (id: string) =>
    request<Envelope<MeetingTranscriptResult>>(`/meetings/${id}/transcript`),

  /**
   * 批量改名说话人：把某场会里所有该标签的分段一并改名（如「说话人 1」→
   * 「张律师」）。说话人分离未启用时用占位标签，此接口是唯一的改名入口。
   */
  renameTranscriptSpeaker: (id: string, data: MeetingTranscriptSpeakerRenameRequest) =>
    request<Envelope<{ updated: number }>>(`/meetings/${id}/transcript/speakers`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  /** 逐段编辑：纠正 ASR 误识别的文字、改单段说话人、切换高亮。 */
  updateTranscriptSegments: (id: string, data: MeetingTranscriptSegmentsEditRequest) =>
    request<Envelope<MeetingTranscriptResult>>(`/meetings/${id}/transcript/segments`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  /**
   * Import an existing audio file (vs live recording) and auto-trigger ASR.
   * Multipart upload — same shape as `uploadAudio` but routes through the
   * `/audio/import` endpoint which enqueues the transcription task on top.
   */
  importAudio: (
    id: string,
    file: File,
    options?: {
      durationSeconds?: number;
      recordedAt?: string;
      autoTranscribe?: boolean;
    },
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    if (options?.durationSeconds !== undefined) {
      formData.append("duration_seconds", String(options.durationSeconds));
    }
    if (options?.recordedAt) formData.append("recorded_at", options.recordedAt);
    if (options?.autoTranscribe !== undefined) {
      formData.append("auto_transcribe", String(options.autoTranscribe));
    }
    return request<
      Envelope<{ meeting: Meeting; taskId: string | null }>
    >(`/meetings/${id}/audio/import`, {
      method: "POST",
      body: formData,
      skipContentType: true,
      timeoutMs: 300_000,
    });
  },

  listConnections: () =>
    request<Envelope<MeetingConnection[]>>("/meetings/connections"),

  beginOAuth: (source: MeetingSource) =>
    request<Envelope<{ authorizeUrl: string }>>(`/meetings/oauth/authorize/${source}`),

  /** 设备授权（CLI 同款通道，免应用注册）：返回授权链接，前端轮询连接状态。 */
  startDeviceAuth: (source: MeetingSource) =>
    request<Envelope<{ authorizeUrl: string; expiresIn: number }>>(
      `/meetings/oauth/device/${source}/start`,
      { method: "POST" },
    ),

  /** 在会议平台上预订会议（显式写操作）。 */
  book: (payload: MeetingBookPayload) =>
    request<Envelope<Meeting>>("/meetings/book", {
      method: "POST",
      body: JSON.stringify({
        source: payload.source,
        subject: payload.subject,
        starts_at: payload.startsAt.toISOString(),
        ends_at: payload.endsAt.toISOString(),
        recurring_rule: payload.recurringRule
          ? {
              recurring_type: payload.recurringRule.recurringType,
              until_type: payload.recurringRule.untilType,
              until_count: payload.recurringRule.untilCount ?? undefined,
              until_date: payload.recurringRule.untilDate,
            }
          : undefined,
        password: payload.password,
      }),
    }),

  disconnect: (source: MeetingSource) =>
    request<Envelope<{ source: string }>>(`/meetings/connections/${source}`, {
      method: "DELETE",
    }),

  sync: (source: MeetingSource, params?: { dateFrom?: string; dateTo?: string }) => {
    const body: Record<string, unknown> = { source };
    if (params?.dateFrom) body.date_from = params.dateFrom;
    if (params?.dateTo) body.date_to = params.dateTo;
    return request<
      Envelope<{
        source: string;
        upcoming: number;
        historical: number;
        created: number;
        updated: number;
        error?: string;
      }>
    >("/meetings/sync", { method: "POST", body: JSON.stringify(body) });
  },

  syncAll: () =>
    request<
      Envelope<{
        sources_synced: number;
        total_created: number;
        total_updated: number;
        results: Array<{
          source: string;
          upcoming: number;
          historical: number;
          created: number;
          updated: number;
          error?: string;
        }>;
      }>
    >("/meetings/sync-all", { method: "POST" }),

  /**
   * 逐场导入会议内容：在会议清单里选中这一场，把纪要拉进来。
   *
   * 同步只带回清单信息，纪要由本动作导入。平台纪要尚在生成时会议留在「转录中」，
   * 不是失败，稍后重试即可。
   */
  importContent: (id: string, data: MeetingContentImportRequest) =>
    request<Envelope<Meeting>>(`/meetings/${id}/import`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  /**
   * 归档会议纪要：产出一份进文档库、可被检索的纪要文档。
   *
   * 前置条件是会后总结已就绪。未绑定事项的会议照常可归档，纪要落归档人的个人资料。
   */
  archive: (id: string) =>
    request<Envelope<Meeting>>(`/meetings/${id}/archive`, { method: "POST" }),

  /**
   * 本人确认纪要：个人阶段的确认即律师对自己的 AI 草稿点头，无组织审批语义。
   * 确认后待办确认、归档动作解锁。同一会议重复确认或状态不满足前置条件时
   * 返回 409，调用方据此提示并刷新会议详情。
   */
  confirm: (id: string) =>
    request<Envelope<Meeting>>(`/meetings/${id}/confirm`, { method: "POST" }),

  /** 撤销本人确认：退回未确认状态，已归档的会议不可撤销（后端拒绝）。 */
  revokeConfirmation: (id: string) =>
    request<Envelope<Meeting>>(`/meetings/${id}/revoke-confirmation`, {
      method: "POST",
    }),

  /**
   * 提交一次 AI 产出，立即返回（202），结果由 `getActionState` 轮询。
   *
   * 三个动作的状态各自独立，一个在途不阻止另外两个提交；同一动作重复提交返回 409。
   */
  submitAction: (id: string, action: MeetingAiAction) =>
    request<Envelope<Record<string, unknown>>>(
      `/meetings/${id}/actions/${action}`,
      { method: "POST" },
    ).then((res) => ({
      ...res,
      // 该端点返回的是服务层原始字典（下划线命名），不经 schema 序列化。
      data: toCamelCase(res.data) as unknown as MeetingAiActionSubmitResult,
    })),

  /**
   * 一个动作的产出状态与结果。`result` 只在状态为「已就绪」时有值——失败与超时
   * 不写结果，上一次的产出仍留在会议本身与候选待办列表里。
   */
  getActionState: (id: string, action: MeetingAiAction) =>
    request<Envelope<Record<string, unknown>>>(
      `/meetings/${id}/actions/${action}`,
    ).then((res) => ({
      ...res,
      data: toCamelCase(res.data) as unknown as MeetingAiActionState,
    })),

  /** 本会议下尚未确认的候选待办，供待办确认界面逐条勾选。 */
  listTaskCandidates: (id: string) =>
    request<Envelope<MeetingTaskCandidate[]>>(`/meetings/${id}/task-candidates`),

  /** 逐条确认候选待办：保留的转为正式任务，未保留的丢弃。整批生效或整批不生效。 */
  confirmTaskCandidates: (id: string, decisions: MeetingTaskCandidateDecision[]) =>
    request<Envelope<Record<string, unknown>>>(
      `/meetings/${id}/task-candidates/confirm`,
      { method: "POST", body: JSON.stringify({ decisions }) },
    ).then((res) => ({
      ...res,
      data: toCamelCase(res.data) as unknown as MeetingTaskCandidateConfirmResult,
    })),
};

// ---------------------------------------------------------------------------
// Due Diligence（尽职调查）— `jerry_due_diligence_projects`, path prefix
// /due-diligence. v1 单业务方向单路调研，参见：
//   docs/decisions/2026-07-09-尽职调查-v1-决策.md
//   docs/iterations/due-diligence-v1-plan.md
// ---------------------------------------------------------------------------

export const dueDiligenceProjectsApi = {
  /** 列表——当前用户可见项目（本人创建 ∪ 所属事项），更新时间倒序。 */
  list: (params?: DueDiligenceProjectListParams) => {
    const qs = new URLSearchParams();
    if (params?.matterId) qs.set("matterId", params.matterId);
    if (params?.unassigned) qs.set("unassigned", "true");
    if (params?.scopeMode) qs.set("scopeMode", params.scopeMode);
    if (params?.status) qs.set("status", params.status);
    if (params?.q) qs.set("q", params.q);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<DueDiligenceProjectListItem>>(
      `/due-diligence/projects?${qs}`,
    );
  },

  /** 新建项目（clientPartyId 业务必填，matterId 可选）。 */
  create: (data: DueDiligenceProjectCreatePayload) =>
    request<Envelope<DueDiligenceProject>>(`/due-diligence/projects`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  /** 项目详情（含 subReports 与 materials）。 */
  get: (projectId: string) =>
    request<Envelope<DueDiligenceProject>>(`/due-diligence/projects/${projectId}`),

  /** 更新元数据或取消（status=cancelled 仅 dispatched/collecting 可取消）。 */
  update: (projectId: string, data: DueDiligenceProjectUpdatePayload) =>
    request<Envelope<DueDiligenceProject>>(`/due-diligence/projects/${projectId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  /** 软删除（仅 draft/cancelled/failed 状态可删）。 */
  remove: (projectId: string) =>
    request<Envelope<null>>(`/due-diligence/projects/${projectId}`, {
      method: "DELETE",
    }),

  /** 把文档库里的既有文档关联为尽调资料（1A-3）；重复关联幂等，返回项目详情。 */
  attachMaterials: (projectId: string, documentIds: string[]) =>
    request<Envelope<DueDiligenceProject>>(
      `/due-diligence/projects/${projectId}/materials`,
      { method: "POST", body: JSON.stringify({ documentIds }) },
    ),

  /** 解除一份资料与项目的关联（只删关联，不动文档）。 */
  detachMaterial: (projectId: string, documentId: string) =>
    request<{ success: boolean }>(
      `/due-diligence/projects/${projectId}/materials/${documentId}`,
      { method: "DELETE" },
    ),

  /** 列项目下全部子报告（v1 每项目 1 行；用于 SSE 断线后轮询对账）。 */
  listSubReports: (projectId: string) =>
    request<Envelope<DueDiligenceSubReport[]>>(
      `/due-diligence/projects/${projectId}/sub-reports`,
    ),

  /** 取单个子报告详情（含 body 与 citations）。 */
  getSubReport: (projectId: string, subReportId: string) =>
    request<Envelope<DueDiligenceSubReport>>(
      `/due-diligence/projects/${projectId}/sub-reports/${subReportId}`,
    ),

  /** 信息收集会话历史（引擎持久化；draft 状态返回空）。 */
  listMessages: (projectId: string) =>
    request<Envelope<DueDiligenceMessage[]>>(
      `/due-diligence/projects/${projectId}/messages`,
    ).then((res) => res.data),

  /**
   * 启动调研（v1 单路直派）。前置：collecting + businessDirection。
   * 返回新建 sub_report（status=pending）；前端通过 SSE 订阅状态变化。
   */
  dispatch: (projectId: string) =>
    request<Envelope<DueDiligenceSubReport>>(
      `/due-diligence/projects/${projectId}/dispatch`,
      { method: "POST", body: JSON.stringify({}) },
    ).then((res) => res.data),

  /** 失败重试——产生新 sub_report 行（retry_of_id 血缘），重新派发。 */
  retrySubReport: (projectId: string, subReportId: string) =>
    request<Envelope<DueDiligenceSubReport>>(
      `/due-diligence/projects/${projectId}/sub-reports/${subReportId}/retry`,
      { method: "POST", body: JSON.stringify({}) },
    ).then((res) => res.data),

  /**
   * 导出子报告为 Markdown（顶栏「导出」）。v1 仅 markdown；返回 Blob 供
   * 浏览器下载。前置：succeeded + body 非空。
   */
  exportSubReport: async (projectId: string, subReportId: string) => {
    const blob = await request<Blob>(
      `/due-diligence/projects/${projectId}/sub-reports/${subReportId}/export`,
      { method: "GET", returnBlob: true },
    );
    return blob;
  },

  /** 把子报告正文另存为事项文档（复用 US-D02 落库链路）。 */
  saveSubReportAsDocument: (
    projectId: string,
    subReportId: string,
    data: DueDiligenceSubReportSaveAsDocumentPayload,
  ) =>
    request<Envelope<Document>>(
      `/due-diligence/projects/${projectId}/sub-reports/${subReportId}/save-as-document`,
      {
        method: "POST",
        body: JSON.stringify({
          document_name: data.documentName,
          body: data.body,
          matter_id: data.matterId,
        }),
      },
    ).then((res) => res.data),

  // -------------------------------------------------------------------------
  // 模式 B：公开信息调研（public-intel-research v1）—— 平行新增端点，
  // 不修改任何模式 A 端点（RC-2）。参见 docs/iterations/public-intel-research-v1-plan.md
  // -------------------------------------------------------------------------

  /**
   * 启动公开调研（模式 B）。返回新建 sub_report（status=pending）。
   * 与模式 A 的 dispatch 完全独立：模式 A 项目不能调用本端点（service 层拒绝）。
   */
  dispatchPublicResearch: (projectId: string) =>
    request<Envelope<DueDiligenceSubReport>>(
      `/due-diligence/projects/${projectId}/dispatch-public`,
      { method: "POST", body: JSON.stringify({}) },
    ).then((res) => res.data),

  /**
   * 已完成项目重新调查（仅 PIR 模式 + status=completed）。
   * body.research_question 为空时沿用原值；非空且不同则更新后派发。
   * 返回新建 sub_report（status=pending），血缘 retry_of_id 指向最新 succeeded。
   */
  reResearch: (
    projectId: string,
    body?: { research_question?: string | null },
  ) =>
    request<Envelope<DueDiligenceSubReport>>(
      `/due-diligence/projects/${projectId}/re-research`,
      { method: "POST", body: JSON.stringify(body ?? {}) },
    ).then((res) => res.data),

  /**
   * 列出公开调研来源引用（按 source_type / credibility 可选筛选）。
   * 响应 envelope 内 data 为 { items, total } 结构（与列表 envelope 解构一致）。
   */
  listPublicResearchSources: (
    projectId: string,
    params?: PublicResearchSourcesParams,
  ) => {
    const qs = new URLSearchParams();
    if (params?.subReportId) qs.set("sub_report_id", params.subReportId);
    if (params?.sourceType) qs.set("source_type", params.sourceType);
    if (params?.credibility) qs.set("credibility", params.credibility);
    return request<Envelope<PublicResearchSourcesResponse>>(
      `/due-diligence/projects/${projectId}/public-research/sources${
        qs.toString() ? `?${qs}` : ""
      }`,
    ).then((res) => res.data);
  },
};

// ---------------------------------------------------------------------------
// Calendar (底座 calendar 模块，prefix /api/v1/calendar)
//
// 与 jerry 模块不同，calendar 模块的响应字段是 snake_case（未启用 alias_generator）。
// 这里在 API 层做 snake_case → camelCase 转换，使前端 types 保持项目惯例（全 camelCase）。
// ---------------------------------------------------------------------------

const CAL_PREFIX = "/api/v1/calendar";

function camelKey(snake: string): string {
  return snake.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
}

function snakeKey(camel: string): string {
  return camel.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
}

function mapKeysToCamel<T>(obj: T): T {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(mapKeysToCamel) as unknown as T;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    // `extra` is an application-defined JSONB bag (keyed by EventType.schema),
    // not part of the calendar API contract — preserve its key shape so caller
    // extensions like { caseNumber, hearing_type } round-trip untouched.
    out[camelKey(k)] = k === "extra" ? v : mapKeysToCamel(v);
  }
  return out as T;
}

function mapKeysToSnake<T>(obj: T): T {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(mapKeysToSnake) as unknown as T;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    out[snakeKey(k)] = k === "extra" ? v : mapKeysToSnake(v);
  }
  return out as T;
}

export const calendarApi = {
  // -- Calendars -----------------------------------------------------------

  /** List the current user's non-deleted calendars. */
  listCalendars: () =>
    request<Envelope<Calendar[]>>(`/calendars`, undefined, CAL_PREFIX).then(
      (res) => res.data.map(mapKeysToCamel),
    ),

  /** Create a new calendar for the current user. */
  createCalendar: (payload: CalendarCreate) =>
    request<Envelope<Calendar>>(
      `/calendars`,
      { method: "POST", body: JSON.stringify(mapKeysToSnake(payload)) },
      CAL_PREFIX,
    ).then((res) => mapKeysToCamel(res.data)),

  /** Update calendar metadata (name, color, visibility, default flag). */
  updateCalendar: (id: string, payload: CalendarUpdate) =>
    request<Envelope<Calendar>>(
      `/calendars/${id}`,
      { method: "PUT", body: JSON.stringify(mapKeysToSnake(payload)) },
      CAL_PREFIX,
    ).then((res) => mapKeysToCamel(res.data)),

  /** Soft-delete a calendar. */
  deleteCalendar: (id: string) =>
    request<Envelope<null>>(
      `/calendars/${id}`,
      { method: "DELETE" },
      CAL_PREFIX,
    ).then((res) => res.data),

  /** Trigger an ICS file download for a calendar. Returns a Blob. */
  exportCalendarIcs: (id: string) =>
    request<Blob>(
      `/calendars/${id}/export.ics`,
      { returnBlob: true },
      CAL_PREFIX,
    ),

  // -- Events --------------------------------------------------------------

  /** List expanded event instances within [from, to). */
  listEvents: (params: ListEventsParams) => {
    const qs = new URLSearchParams();
    qs.set("from", params.from);
    qs.set("to", params.to);
    if (params.calendarId) qs.set("calendar_id", params.calendarId);
    if (params.eventType) qs.set("event_type", params.eventType);
    return request<Envelope<EventInstance[]>>(
      `/events?${qs}`,
      undefined,
      CAL_PREFIX,
    ).then((res) => res.data.map(mapKeysToCamel));
  },

  /** Create a new calendar event (single or recurring). */
  createEvent: (payload: CalendarEventCreate, calendarId: string) => {
    const qs = new URLSearchParams({ calendar_id: calendarId });
    return request<Envelope<CalendarEvent>>(
      `/events?${qs}`,
      {
        method: "POST",
        body: JSON.stringify(mapKeysToSnake({ ...payload, ref: payload.ref })),
      },
      CAL_PREFIX,
    ).then((res) => mapKeysToCamel(res.data));
  },

  /** Retrieve a single event with its reminders. */
  getEvent: (id: string) =>
    request<Envelope<CalendarEvent>>(
      `/events/${id}`,
      undefined,
      CAL_PREFIX,
    ).then((res) => mapKeysToCamel(res.data)),

  /** Update a calendar event. scope="all" modifies the master; scope="this"
   *  upserts an override for one occurrence (requires occurrenceStart).
   *  The backend returns the full master CalendarEvent for scope="all" and a
   *  slim override descriptor for scope="this" — see CalendarEventUpdateResult. */
  updateEvent: (
    id: string,
    payload: CalendarEventUpdate,
    options?: UpdateEventOptions,
  ): Promise<CalendarEventUpdateResult> => {
    const qs = new URLSearchParams();
    qs.set("scope", options?.scope ?? "all");
    if (options?.scope === "this" && options.occurrenceStart) {
      qs.set("occurrence_start", options.occurrenceStart);
    }
    return request<Envelope<CalendarEventUpdateResult>>(
      `/events/${id}?${qs}`,
      { method: "PUT", body: JSON.stringify(mapKeysToSnake(payload)) },
      CAL_PREFIX,
    ).then((res) => mapKeysToCamel(res.data));
  },

  /** Delete a calendar event. scope="all" soft-deletes the master;
   *  scope="this" cancels one occurrence via an override. */
  deleteEvent: (id: string, options?: DeleteEventOptions) => {
    const qs = new URLSearchParams();
    qs.set("scope", options?.scope ?? "all");
    if (options?.scope === "this" && options.occurrenceStart) {
      qs.set("occurrence_start", options.occurrenceStart);
    }
    return request<Envelope<null>>(
      `/events/${id}?${qs}`,
      { method: "DELETE" },
      CAL_PREFIX,
    ).then((res) => res.data);
  },

  // -- Reminders -----------------------------------------------------------

  /** Add a reminder to an existing event. */
  addReminder: (eventId: string, payload: EventReminderCreate) =>
    request<Envelope<EventReminder>>(
      `/events/${eventId}/reminders`,
      { method: "POST", body: JSON.stringify(mapKeysToSnake(payload)) },
      CAL_PREFIX,
    ).then((res) => mapKeysToCamel(res.data)),

  /** Soft-delete a reminder. */
  deleteReminder: (eventId: string, reminderId: string) =>
    request<Envelope<null>>(
      `/events/${eventId}/reminders/${reminderId}`,
      { method: "DELETE" },
      CAL_PREFIX,
    ).then((res) => res.data),

  // -- Event Types ---------------------------------------------------------

  /** List active event types (global registry). */
  listEventTypes: () =>
    request<Envelope<EventType[]>>(
      `/event-types`,
      undefined,
      CAL_PREFIX,
    ).then((res) => res.data.map(mapKeysToCamel)),
};

// ---------------------------------------------------------------------------
// IPO Vault (境内外 IPO 宝库)
// ---------------------------------------------------------------------------

export const ipoVaultApi = {
  /** Get overview statistics and metadata. */
  overview: () =>
    request<{ success: boolean; data: unknown; meta?: unknown }>(`/ipo-vault/overview`)
      .then((res) => {
        const stats = toCamelCase(res.data) as Overview["stats"];
        const meta = toCamelCase(res.meta ?? {}) as Overview["meta"];
        return { ...res, data: { stats, meta } as Overview };
      }),

  /** List legal issues with filters. */
  legalIssues: (params?: LegalIssuesParams) => {
    const qs = new URLSearchParams();
    if (params?.market) qs.set("market", params.market);
    if (params?.board) qs.set("board", params.board);
    if (params?.issueCategory) qs.set("category", params.issueCategory);
    if (params?.issueYear) qs.set("year", String(params.issueYear));
    if (params?.projectStatus) qs.set("status", params.projectStatus);
    if (params?.keyword) qs.set("q", params.keyword);
    if (params?.companyName) qs.set("company", params.companyName);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<LegalIssue>>(`/ipo-vault/legal-issues?${qs}`)
      .then((res) => ({ ...res, data: toCamelCase(res.data) as LegalIssue[] }));
  },

  /** Get legal issue facets for filter options. */
  legalIssueFacets: (params?: Pick<LegalIssuesParams, "market" | "board" | "issueYear" | "projectStatus">) => {
    const qs = new URLSearchParams();
    if (params?.market) qs.set("market", params.market);
    if (params?.board) qs.set("board", params.board);
    if (params?.issueYear) qs.set("year", String(params.issueYear));
    if (params?.projectStatus) qs.set("status", params.projectStatus);
    return request<{ success: boolean; data: LegalIssueFacets }>(`/ipo-vault/legal-issues/facets?${qs}`)
      .then((res) => ({ ...res, data: toCamelCase(res.data) as LegalIssueFacets }));
  },

  /** Get single legal issue detail with extra and detail JSONB. */
  legalIssue: (uid: string) =>
    request<{ success: boolean; data: LegalIssue }>(`/ipo-vault/legal-issues/${uid}`)
      .then((res) => ({ ...res, data: toCamelCase(res.data) as LegalIssue })),

  /** List CSRC filings by category. */
  csrcFilings: (category: string, params?: CsrcFilingsParams) => {
    const qs = new URLSearchParams();
    if (category) qs.set("category", category);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<CsrcFiling>>(`/ipo-vault/csrc/filings?${qs}`)
      .then((res) => ({ ...res, data: toCamelCase(res.data) as CsrcFiling[] }));
  },

  /** List CSRC feedback with filters. */
  csrcFeedback: (params?: CsrcFeedbackParams) => {
    const qs = new URLSearchParams();
    if (params?.primaryCategory) qs.set("category", params.primaryCategory);
    if (params?.feedbackYear) qs.set("year", String(params.feedbackYear));
    if (params?.companyName) qs.set("company", params.companyName);
    if (params?.keyword) qs.set("q", params.keyword);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<CsrcFeedback>>(`/ipo-vault/csrc/feedback?${qs}`)
      .then((res) => ({ ...res, data: toCamelCase(res.data) as CsrcFeedback[] }));
  },

  /** Get CSRC feedback facets. */
  csrcFeedbackFacets: () =>
    request<{ success: boolean; data: CsrcFeedbackFacets }>(`/ipo-vault/csrc/feedback/facets`)
      .then((res) => ({ ...res, data: toCamelCase(res.data) as CsrcFeedbackFacets })),

  /** Get filing timeline snapshot. */
  csrcTimeline: () =>
    request<{ success: boolean; data: TimelineSnapshot }>(`/ipo-vault/csrc/timeline`)
      .then((res) => ({ ...res, data: toCamelCase(res.data) as TimelineSnapshot })),

  /** List HKEX new listing cases by category. */
  hkexCases: (category: string, params?: HkexCasesParams) => {
    const qs = new URLSearchParams();
    if (category) qs.set("category", category);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<HkexCase>>(`/ipo-vault/hkex/cases?${qs}`)
      .then((res) => ({ ...res, data: toCamelCase(res.data) as HkexCase[] }));
  },

  /** List HKEX announcements with filters. */
  hkexAnnouncements: (params?: HkexAnnouncementsParams) => {
    const qs = new URLSearchParams();
    if (params?.stockCode) qs.set("stock_code", params.stockCode);
    if (params?.startDate) qs.set("date_from", params.startDate);
    if (params?.endDate) qs.set("date_to", params.endDate);
    if (params?.keyword) qs.set("q", params.keyword);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<HkexAnnouncement>>(`/ipo-vault/hkex/announcements?${qs}`)
      .then((res) => ({ ...res, data: toCamelCase(res.data) as HkexAnnouncement[] }));
  },

  /** List SEC filings by form type. */
  sec: (formType: string, params?: SecFilingsParams) => {
    const qs = new URLSearchParams();
    if (formType) qs.set("form_type", formType);
    if (params?.companyName) qs.set("company", params.companyName);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<SecFiling>>(`/ipo-vault/sec?${qs}`)
      .then((res) => ({ ...res, data: toCamelCase(res.data) as SecFiling[] }));
  },

  /** List sync run history. */
  syncRuns: (params?: SyncRunsParams) => {
    const qs = new URLSearchParams();
    if (params?.dataset) qs.set("dataset", params.dataset);
    if (params?.status) qs.set("status", params.status);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<PaginatedResponse<SyncRun>>(`/ipo-vault/sync/runs?${qs}`)
      .then((res) => ({ ...res, data: toCamelCase(res.data) as SyncRun[] }));
  },

  /** Trigger incremental sync. */
  triggerSync: () =>
    request<{ success: boolean; data: SyncTriggerResponse }>(`/ipo-vault/sync/trigger`, { method: "POST" })
      .then((res) => ({ ...res, data: toCamelCase(res.data) as SyncTriggerResponse })),

  /** Trigger full backfill. */
  triggerBackfill: () =>
    request<{ success: boolean; data: SyncTriggerResponse }>(`/ipo-vault/sync/backfill`, { method: "POST" })
      .then((res) => ({ ...res, data: toCamelCase(res.data) as SyncTriggerResponse })),

  // -------------------------------------------------------------------------
  // Insights (Professional Intelligence Layer)
  // -------------------------------------------------------------------------

  /** Get audit trends by board and months. */
  insightsTrends: (months: number) =>
    request<{ success: boolean; data: TrendsData; meta: TrendsMeta }>(`/ipo-vault/insights/trends?months=${months}`)
      .then((res) => ({ ...res, data: toCamelCase(res.data) as TrendsData, meta: toCamelCase(res.meta ?? {}) as TrendsMeta })),

  /** Get inquiry hotspots by months, optional market/board filter. */
  insightsHotspots: (months: number, market?: string, board?: string) => {
    const params = new URLSearchParams({ months: months.toString() });
    if (market) params.set("market", market);
    if (board) params.set("board", board);
    return request<{ success: boolean; data: HotspotsData; meta: HotspotsMeta }>(`/ipo-vault/insights/hotspots?${params}`)
      .then((res) => ({ ...res, data: toCamelCase(res.data) as HotspotsData, meta: toCamelCase(res.meta ?? {}) as HotspotsMeta }));
  },

  /** Get board profile by market, board, and months. */
  insightsBoardProfile: (market: string, board?: string, months?: number) => {
    const params = new URLSearchParams({ market });
    if (board) params.set("board", board);
    if (months) params.set("months", months.toString());
    return request<{ success: boolean; data: BoardProfileData; meta: BoardProfileMeta }>(`/ipo-vault/insights/board-profile?${params}`)
      .then((res) => ({ ...res, data: toCamelCase(res.data) as BoardProfileData, meta: toCamelCase(res.meta ?? {}) as BoardProfileMeta }));
  },

  /** Get board benchmarks. */
  insightsBenchmarks: () =>
    request<{ success: boolean; data: BenchmarksData; meta: BenchmarksMeta }>(`/ipo-vault/insights/benchmarks`)
      .then((res) => ({ ...res, data: toCamelCase(res.data) as BenchmarksData, meta: toCamelCase(res.meta ?? {}) as BenchmarksMeta })),

  // -------------------------------------------------------------------------
  // Company Search & Detail
  // -------------------------------------------------------------------------

  /** Search companies by name or keyword. */
  companySearch: (q: string, limit = 10) =>
    request<{ success: boolean; data: CompanySearchResult[] }>(`/ipo-vault/companies?q=${encodeURIComponent(q)}&limit=${limit}`)
      .then((res) => ({ ...res, data: toCamelCase(res.data) as CompanySearchResult[] })),

  /** Get company detail by name. */
  companyDetail: (name: string) =>
    request<{ success: boolean; data: CompanyDetailData; meta: CompanyDetailMeta }>(`/ipo-vault/companies/${encodeURIComponent(name)}`)
      .then((res) => ({ ...res, data: toCamelCase(res.data) as CompanyDetailData, meta: toCamelCase(res.meta ?? {}) as CompanyDetailMeta })),
};
